import { Head, router, usePage } from '@inertiajs/react'
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import '@xyflow/react/dist/style.css'
import { Search, MoreVertical, Archive, Copy as CopyIcon, Edit2, Trash2, RotateCcw, FileDown, Loader2, Globe, EyeOff, UserCheck, LayoutGrid, Plus, Undo2, Redo2 } from 'lucide-react'
import Layout from '../../components/Layout'
import ConfirmModal from '../../components/shared/ConfirmModal'
import FloatingMenu from '../../components/shared/FloatingMenu'
import { FILLED_REQUIRES_OWNER, LABELS, NAVIGATOR_LABELS, indefiniteArticle, navigatorSummary } from './labels'
import { NavigatorBadge, NavigatorLegend } from './NavigatorIndicator'
import { exportChartPdf } from './exportChartPdf'
import { SeatNode } from './SeatNode'
import { ConnectorEdge, CONNECTOR_COLOR, CONNECTOR_SELECTED_COLOR, dragCurvePath } from './ConnectorEdge'
import type { ConnectorEdgeData } from './ConnectorEdge'
import SeatDetailPanel from './SeatDetailPanel'
import { hasStoredPosition, layoutChart, placeUnderTarget, PositionedNode } from './chartLayout'
import type { CardDensity } from './cardMetrics'
import { resolveDrop } from './dropTargets'
import type { DropHint, DropResolution } from './dropTargets'
import { branchSize, resolveConnectorEndpoint, resolveNewConnectorEndpoint } from './connectorEdits'
import type { ConnectorResolution } from './connectorEdits'
import {
  GUIDELINE_DOT_SIZE,
  GUIDELINE_DRAG_DOT_SIZE,
  GUIDELINE_SPACING,
  guidelineDotOffset,
  resolveSnap,
} from './guidelines'
import type { GuidelineIndication, SnapResult } from './guidelines'
import GuidelineSnapLayer from './GuidelineSnapLayer'
import { applySeatMove, heldPlacementParams, siblingsOf } from './seatMoves'
import type { SeatMove } from './seatMoves'
import { SelectionToolbarLayer } from './SelectionToolbar'
import { ALIGNMENT_LABELS, applyAlignment } from './chartAlignment'
import type { AlignmentAction, AlignmentBox } from './chartAlignment'
import { coordinatesOf, placementOf, snapshotSeat, useChartHistory } from './chartHistory'
import type { ChartFieldEdit, ChartHistoryEntry, SeatCoordinates, SeatPlacement } from './chartHistory'
import { createChartMutationQueue, createSerializedCollapsedStateSaver, inertiaErrorMessage } from './chartStatePersistence'
import type { ChartMutation, ChartMutationQueue } from './chartStatePersistence'
import type { SharedProps } from '../../types'
import type { ChartDetail, SeatDetail, UserPickerEntry, SeatTag } from './types'
import { ReactFlow, Background, BackgroundVariant, Controls, MiniMap, useNodesState, useEdgesState, ReactFlowProvider, MarkerType, SelectionMode, useReactFlow } from '@xyflow/react'
import ZoomReadout from './ZoomReadout'
import { CHART_MAX_ZOOM, CHART_MIN_ZOOM, useSmoothWheelZoom } from './smoothZoom'
import { CARD_EXIT_MS, prefersReducedMotion, useEasedPoint, withLeavingNodes, withoutLeavingNodes } from './canvasMotion'
import { useResizeReflow, withDisplayedRoutes, REFLOW_MS } from './resizeReflow'
import { familyRoutes } from './familyRoutes'
import { CARD_WIDTH, CHART_BOTTOM_CLUSTER_H, CHART_FIT_PADDING, MIN_LEGIBLE_ZOOM } from './cardMetrics'

interface Props {
  active_tab: string
  chart: ChartDetail
  user_picker_team: UserPickerEntry[]
  user_picker_org: UserPickerEntry[]
  org_tags: SeatTag[]
}

/** The page an Inertia visit answered with, as this page reads it. */
type InertiaPage = { props?: unknown }
interface ChartPageProps {
  chart?: ChartDetail
  flash?: SharedProps['flash']
}

const nodeTypes = { seat: SeatNode }
const edgeTypes = { connector: ConnectorEdge }

const DROP_LABELS = { seat: LABELS.seat.toLowerCase(), seats: LABELS.seats.toLowerCase() }

/** What a box added from a role's menu or the canvas toolbar is called until it is renamed. */
const NEW_SEAT_NAME = 'New Role'

/**
 * Why an edit is refused while the tidy tree is on screen. The preview is an
 * arrangement nobody has agreed to yet, so a change made against it would be
 * saved against a layout the user cannot see.
 */
const AUTO_LAYOUT_BLOCKED = 'Apply or cancel the automatic layout before changing the chart.'

/**
 * How much of the viewport the page chrome above the canvas takes: the app's
 * own header and tabs plus the chart's top bar. The canvas is sized by
 * subtraction rather than by flexing, so both numbers have to be stated.
 */
const CHART_CHROME_HEIGHT = 220
/** The canvas toolbar row, which only someone who can change the chart gets. */
const CANVAS_TOOLBAR_HEIGHT = 51

/**
 * What undo calls a move made from a role's own menu. The words are the menu
 * items' own, lower-cased to sit inside "Undo …" like every other history
 * label, so taking back a Promote says promote rather than a generic "move".
 */
const SEAT_MENU_MOVE_LABELS: Record<string, string> = {
  'move-up': 'move up',
  'move-down': 'move down',
  promote: 'promote',
  demote: 'demote',
}
export default function AccountabilityChartShow(props: Props) {
  return (
    <ReactFlowProvider>
      <ShowInner {...props} />
    </ReactFlowProvider>
  )
}

function ShowInner({ chart, user_picker_team, user_picker_org, org_tags }: Props) {
  const { current_organization, flash } = usePage().props as unknown as SharedProps
  // Set once by SeatsController#restore: the id of the box undo just rebuilt,
  // and how many of its comments were re-attached to it.
  const restoredSeatId = flash?.restored_seat_id ?? null
  const restoredCommentCount = flash?.restored_comment_count ?? 0
  const basePath = `/accountability_charts/${chart.id}`

  const canManage = chart.permissions.can_manage
  const { setViewport, getViewport, screenToFlowPosition } = useReactFlow()
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set(chart.user_chart_state.collapsed_seat_ids))
  // Card density is session view state only — the persisted preference stays
  // the per-branch collapse set.
  const [density, setDensity] = useState<CardDensity>('expanded')
  const [selectedSeat, setSelectedSeat] = useState<SeatDetail | null>(null)
  const [editError, setEditErrorState] = useState<string | null>(null)
  // A drop that deliberately changed nothing. It is not a failure, so it gets a
  // neutral note rather than the red error bar — but it is never silent.
  const [dropNotice, setDropNoticeState] = useState<string | null>(null)
  const setEditError = useCallback((message: string | null) => {
    setEditErrorState(message)
    if (message !== null) setDropNoticeState(null)
  }, [])
  const setDropNotice = useCallback((message: string | null) => {
    setDropNoticeState(message)
    if (message !== null) setEditErrorState(null)
  }, [])
  const [search, setSearch] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(chart.name)
  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; onConfirm: () => void; variant?: 'danger' | 'warning' } | null>(null)
  const [fillToast, setFillToast] = useState<SeatDetail | null>(null)
  const [chartMenu, setChartMenu] = useState(false)
  // Pure view state: marks owners as Navigator users or not. It never filters,
  // reorders or persists anything, and the PDF export ignores it entirely so a
  // printed chart always looks the same.
  const [showNavigatorStatus, setShowNavigatorStatus] = useState(false)
  const showNavigatorStatusRef = useRef(showNavigatorStatus)
  showNavigatorStatusRef.current = showNavigatorStatus
  const chartMenuTriggerRef = useRef<HTMLButtonElement>(null)
  const [exporting, setExporting] = useState(false)

  const chartIdentityRef = useRef(chart.id)
  const previousChartIdRef = useRef(chart.id)
  const mountedRef = useRef(false)
  chartIdentityRef.current = chart.id
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  const reportEditError = useCallback((errors: unknown, fallback = 'Unable to save your changes.') => {
    if (!mountedRef.current || chartIdentityRef.current !== chart.id) return
    setEditError(inertiaErrorMessage(errors, fallback))
  }, [chart.id])

  const mutationQueue = useMemo<ChartMutationQueue>(() => createChartMutationQueue({ router }), [basePath])
  const mutationLocksRef = useRef(new Map<string, symbol>())
  const enqueueChartMutation = useCallback((
    // A caller may take over the refusal: `onError`/`onCancel` REPLACE the
    // default red bar rather than adding to it, so a caller that rolls its own
    // optimistic state back is responsible for saying why.
    item: ChartMutation & { singleFlight?: boolean },
    fallback = 'Unable to save your changes.',
  ) => {
    if (item.singleFlight && mutationLocksRef.current.has(item.key)) return false
    const lockToken = item.singleFlight ? Symbol(item.key) : null
    if (lockToken) mutationLocksRef.current.set(item.key, lockToken)

    const { singleFlight, onSuccess, onFinish, onError, onCancel, ...queuedItem } = item
    const accepted = mutationQueue.enqueue({
      ...queuedItem,
      onSuccess: page => {
        if (chartIdentityRef.current === chart.id) onSuccess?.(page)
      },
      onError: errors => {
        if (onError) { if (chartIdentityRef.current === chart.id) onError(errors); return }
        reportEditError(errors, fallback)
      },
      onCancel: () => {
        if (onCancel) { if (chartIdentityRef.current === chart.id) onCancel(); return }
        reportEditError({ base: 'This change was interrupted by navigation.' }, fallback)
      },
      onFinish: () => {
        if (singleFlight && mutationLocksRef.current.get(item.key) === lockToken) {
          mutationLocksRef.current.delete(item.key)
        }
        if (chartIdentityRef.current === chart.id) onFinish?.()
      },
    })
    if (!accepted && singleFlight && mutationLocksRef.current.get(item.key) === lockToken) {
      mutationLocksRef.current.delete(item.key)
    }
    return accepted
  }, [mutationQueue, reportEditError])

  useEffect(() => () => {
    mutationLocksRef.current.clear()
    mutationQueue.dispose()
  }, [mutationQueue])

  const collapsedStateSaver = useMemo(() => createSerializedCollapsedStateSaver({
    queue: mutationQueue,
    url: `${basePath}/user_chart_state`,
    onError: errors => reportEditError(errors, 'Unable to save chart display preferences.'),
  }), [basePath, mutationQueue, reportEditError])

  useEffect(() => () => collapsedStateSaver.dispose(), [collapsedStateSaver])

  useEffect(() => {
    if (previousChartIdRef.current === chart.id) return
    previousChartIdRef.current = chart.id
    setCollapsed(new Set(chart.user_chart_state.collapsed_seat_ids))
    setSelectedSeat(null)
    setRenameValue(chart.name)
    setFillToast(null)
    setEditError(null)
    // A different chart's actions are not this chart's to take back.
    setSelectedSeatIds([])
    pendingMenuMoveRef.current = null
    historyRef.current.clear()
  }, [chart.id])

  const exportPdf = async () => {
    if (exporting) return
    if (nodes.length === 0) return
    setExporting(true)
    try {
      await exportChartPdf({
        // A card fading off the chart is already gone as far as the chart is
        // concerned, so it never reaches the page.
        nodes: withoutLeavingNodes(nodes),
        edges,
        ownersBox: chart.show_owners_box ? chart.owners_box : null,
        chartName: chart.name,
        teamName: current_organization?.name || '',
      })
    } finally {
      setExporting(false)
    }
  }

  // A dropped card is reflected optimistically until the move's own request
  // settles; the server response (or its failure) is what clears it.
  const [pendingMove, setPendingMove] = useState<SeatMove | null>(null)
  // Where a card was just dropped, held until its save settles so the card does
  // not jump back to its old coordinates while the request is in flight.
  const [pendingPositions, setPendingPositions] = useState<Record<number, { x: number; y: number }>>({})
  const clearPendingPosition = useCallback((seatId: number) => {
    setPendingPositions(prev => {
      if (!(seatId in prev)) return prev
      const next = { ...prev }
      delete next[seatId]
      return next
    })
  }, [])

  const allSeats = useMemo(() => {
    const arr: SeatDetail[] = [...chart.seats]
    const moved = pendingMove ? applySeatMove(arr, pendingMove) : arr
    if (Object.keys(pendingPositions).length === 0) return moved
    return moved.map(seat => {
      const pending = pendingPositions[seat.id]
      return pending ? { ...seat, layout_x: pending.x, layout_y: pending.y } : seat
    })
  }, [chart.seats, pendingMove, pendingPositions])

  const matchIds = useMemo(() => {
    if (!search.trim()) return null
    const q = search.toLowerCase()
    return new Set(
      allSeats
        .filter(s =>
          s.name.toLowerCase().includes(q) ||
          s.assignments.some(a => a.display_name.toLowerCase().includes(q)) ||
          s.responsibilities.some(r => r.title.toLowerCase().includes(q)) ||
          s.tags.some(t => t.name.toLowerCase().includes(q))
        )
        .map(s => s.id)
    )
  }, [allSeats, search])

  const [nodes, setNodes, onNodesChange] = useNodesState<any>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([])
  const presentationNodes = useResizeReflow(nodes, chart.id)
  const presentationEdges = useMemo(() => {
    const routes = familyRoutes(withoutLeavingNodes(presentationNodes).map(n => ({
      id: n.id, ...n.position, width: n.width ?? CARD_WIDTH, height: n.height ?? 0,
    })), edges)
    return withDisplayedRoutes(edges, presentationNodes).map(e => ({ ...e, data: { ...e.data, route: routes.get(e.id) } }))
  }, [presentationNodes, edges])

  const toggleCollapse = useCallback((id: number) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      setEditError(null)
      collapsedStateSaver.schedule(Array.from(next))
      return next
    })
  }, [collapsedStateSaver])

  // Toolbar expand/collapse changes how much of each card is shown. It never
  // navigates and never writes a preference; the per-branch chevron above is
  // the control that hides descendants.
  const expandAllCards = () => {
    setEditError(null)
    setDensity('expanded')
  }
  const collapseAllCards = () => {
    setEditError(null)
    setDensity('collapsed')
  }

  const showFillToast = (seat: SeatDetail) => setFillToast(seat)
  // The prompt is only honest while the role still has an owner: the added
  // owner may not have committed into the props yet, and an owner removed in
  // the meantime takes the offer away again.
  const fillCandidate = fillToast ? allSeats.find(s => s.id === fillToast.id) ?? null : null
  const canOfferFill = !!fillCandidate && fillCandidate.assignments.length > 0
  const confirmFill = () => {
    if (!fillToast) return
    if (!fillToast.can_edit) { setFillToast(null); return }
    if (!canOfferFill) {
      setFillToast(null)
      setEditError(FILLED_REQUIRES_OWNER)
      return
    }
    setEditError(null)
    enqueueChartMutation({
      key: `seat:${fillToast.id}:fill`,
      url: `${basePath}/seats/${fillToast.id}`,
      method: 'patch',
      data: { seat: { seat_status: 'filled' } },
      singleFlight: true,
      onSuccess: () => setFillToast(null),
    }, `Unable to mark this ${LABELS.seat.toLowerCase()} as filled.`)
  }

  // A role's menu holds the copy it was opened with, which can be a render
  // behind; the placement a move restates is read off the one on screen now.
  const placedSeat = (seat: SeatDetail) => allSeats.find(s => s.id === seat.id) ?? seat

  const handleSeatAction = (action: string, seat: SeatDetail) => {
    if (!seat.can_edit) return
    // The tidy tree on screen is a preview of an arrangement nobody has agreed
    // to yet, so a change made against it would be saved against a layout the
    // user cannot see.
    if (autoLayoutPreview) {
      setEditError(AUTO_LAYOUT_BLOCKED)
      return
    }
    setEditError(null)
    switch (action) {
      case 'add-below':
        enqueueChartMutation({
          key: `seat:${seat.id}:add-child`,
          url: `${basePath}/seats`,
          method: 'post',
          data: { seat: { parent_seat_id: seat.id, name: NEW_SEAT_NAME } },
          singleFlight: true,
          onSuccess: createRecorder(`add "${NEW_SEAT_NAME}" under "${seat.name}"`),
        })
        break
      case 'add-right':
        enqueueChartMutation({
          key: `seat:${seat.parent_seat_id ?? 'root'}:add-sibling`,
          url: `${basePath}/seats`,
          method: 'post',
          data: { seat: { parent_seat_id: seat.parent_seat_id, name: NEW_SEAT_NAME } },
          singleFlight: true,
          onSuccess: createRecorder(`add "${NEW_SEAT_NAME}" beside "${seat.name}"`),
        })
        break
      case 'move-up':
      case 'move-down': {
        const sibs = allSeats.filter(s => s.parent_seat_id === seat.parent_seat_id).sort((a, b) => a.position - b.position)
        const idx = sibs.findIndex(s => s.id === seat.id)
        const newIdx = action === 'move-up' ? Math.max(0, idx - 1) : Math.min(sibs.length - 1, idx + 1)
        if (newIdx === idx) break
        enqueueChartMutation({
          key: `seat:${seat.id}:move`,
          url: `${basePath}/seats/${seat.id}/move`,
          method: 'post',
          data: { parent_seat_id: seat.parent_seat_id, position: newIdx },
          singleFlight: true,
          onSuccess: menuMoveRecorder(seat, `${SEAT_MENU_MOVE_LABELS[action]} "${seat.name}"`),
        }, `Unable to move this ${LABELS.seat.toLowerCase()}.`)
        break
      }
      case 'promote': {
        const parent = allSeats.find(s => s.id === seat.parent_seat_id)
        if (!parent) break
        enqueueChartMutation({
          key: `seat:${seat.id}:move`,
          url: `${basePath}/seats/${seat.id}/move`,
          method: 'post',
          // Promoting changes the reporting line and nothing else, so a
          // hand-placed box restates where it sits rather than letting the
          // endpoint re-place it under its new parent.
          data: {
            parent_seat_id: parent.parent_seat_id,
            position: (parent.position || 0) + 1,
            ...heldPlacementParams(placedSeat(seat)),
          },
          singleFlight: true,
          onSuccess: menuMoveRecorder(seat, `${SEAT_MENU_MOVE_LABELS.promote} "${seat.name}"`),
        }, `Unable to move this ${LABELS.seat.toLowerCase()}.`)
        break
      }
      case 'demote': {
        const sibs = allSeats.filter(s => s.parent_seat_id === seat.parent_seat_id && s.id !== seat.id).sort((a, b) => a.position - b.position)
        const above = sibs[0]
        if (!above) break
        enqueueChartMutation({
          key: `seat:${seat.id}:move`,
          url: `${basePath}/seats/${seat.id}/move`,
          method: 'post',
          // Same as Promote: the box keeps the spot it was given by hand and
          // only the arrow above it changes.
          data: { parent_seat_id: above.id, position: 0, ...heldPlacementParams(placedSeat(seat)) },
          singleFlight: true,
          onSuccess: menuMoveRecorder(seat, `${SEAT_MENU_MOVE_LABELS.demote} "${seat.name}"`),
        }, `Unable to move this ${LABELS.seat.toLowerCase()}.`)
        break
      }
      case 'move-to':
        setMovePicker({ seat })
        break
      case 'duplicate':
        enqueueChartMutation({
          key: `seat:${seat.id}:duplicate`,
          url: `${basePath}/seats/${seat.id}/duplicate`,
          method: 'post',
          singleFlight: true,
          onSuccess: createRecorder(`duplicate "${seat.name}"`),
        })
        break
      case 'delete':
        setConfirmModal({
          title: `Delete ${LABELS.seat.toLowerCase()}?`,
          message: `This will remove "${seat.name}". Children will be re-parented.`,
          variant: 'danger',
            onConfirm: () => {
              // Taken BEFORE the row goes: the deleted box exists nowhere else
              // afterwards, so this copy is the only thing undo can rebuild from.
              const snapshot = snapshotSeat(seat, allSeats)
              enqueueChartMutation({
                key: `seat:${seat.id}:delete`,
                url: `${basePath}/seats/${seat.id}`,
                method: 'delete',
                singleFlight: true,
                onSuccess: () => {
                  setConfirmModal(null)
                  setSelectedSeat(null)
                  historyRef.current.record({
                    kind: 'delete', label: `delete "${seat.name}"`, seatId: seat.id, snapshot,
                  })
                },
              }, `Unable to delete this ${LABELS.seat.toLowerCase()}.`)
            },
        })
        break
    }
  }

  // `viaConnector` is the same picker reached from an arrow's Redirect item.
  // It goes through the connector's own save so a hand-placed box keeps its
  // coordinates, and its "top level" choice is the confirmed detach.
  const [movePicker, setMovePicker] = useState<{ seat: SeatDetail; viaConnector?: boolean } | null>(null)

  const layoutRef = useRef<PositionedNode[]>([])
  const fitSignatureRef = useRef('')
  const [dropTarget, setDropTarget] = useState<DropResolution | null>(null)
  const lastDragEndRef = useRef(0)
  const paneRef = useRef<HTMLDivElement>(null)
  // The guideline grid only exists during a gesture, so the resting chart stays
  // clean; a refused drop needs the spot the card was picked up from.
  const [draggingSeatId, setDraggingSeatId] = useState<number | null>(null)
  // The lines the box being carried is about to settle onto, decided from the
  // same threshold the release uses so the preview and the correction agree.
  const [snapLines, setSnapLines] = useState<GuidelineIndication | null>(null)
  const snapRef = useRef<SnapResult | null>(null)
  const cancelledDragRef = useRef(false)
  const snapPlacement = useCallback((node: any, moving: any[] = [node]) => {
    const ids = new Set(moving.map(n => String(n.id)))
    const box = (n: any) => {
      const layout = layoutRef.current.find(b => b.id === String(n.id))
      return { id: String(n.id), x: n.position.x, y: n.position.y,
        width: n.width ?? layout?.width ?? CARD_WIDTH, height: n.height ?? layout?.height ?? 0,
        parentId: layout?.seat.parent_seat_id ?? null }
    }
    const others = layoutRef.current.filter(n => !ids.has(n.id)).map(n => ({
      id: n.id, x: n.x, y: n.y, width: n.width, height: n.height, parentId: n.seat.parent_seat_id,
    }))
    return resolveSnap(box(node), others, getViewport().zoom, snapRef.current, moving.map(box))
  }, [getViewport])
  const dragStartRef = useRef<{ id: string; x: number; y: number } | null>(null)
  // Auto Layout shows the tidy tree before anything is saved. While it is on
  // screen the chart is a picture of what Apply would do, so nothing on it can
  // be edited: a drop during the preview would be saved against the real
  // layout, and Cancel would then reveal a placement the user never made.
  const [autoLayoutPreview, setAutoLayoutPreview] = useState(false)
  const [autoLayoutSaving, setAutoLayoutSaving] = useState(false)
  // Moving a card is the one gesture the preview has to withhold: the screen
  // shows an arrangement that is not saved, so a drop would be written against
  // coordinates nobody is looking at.
  const canDragCards = canManage && !autoLayoutPreview

  // ---- Picking out several boxes at once --------------------------------
  //
  // Selection is an editing tool, so it exists only for someone who can move a
  // box. React Flow owns the `selected` flag on each node; this mirror is what
  // the alignment toolbar and the group save read, and what the layout effect
  // puts back after it rebuilds every node from scratch.
  const [selectedSeatIds, setSelectedSeatIds] = useState<number[]>([])
  const selectedSeatIdsRef = useRef<number[]>([])
  selectedSeatIdsRef.current = selectedSeatIds
  const [aligning, setAligning] = useState(false)

  // ---- What can be taken back -------------------------------------------
  //
  // One visit, one history. Nothing is written to the server, so a reload — or
  // someone else's change — simply starts over rather than replaying an action
  // against a chart that has moved on underneath it.
  const history = useChartHistory()
  const historyRef = useRef(history)
  historyRef.current = history
  const [replaying, setReplaying] = useState(false)
  const replayingRef = useRef(false)
  replayingRef.current = replaying
  // The box a restore is rebuilding, held until the server says which id it
  // was given so every entry naming the old one can be renumbered — and so the
  // notice can name it when its conversation comes back with it.
  const awaitingRestoreRef = useRef<{ seatId: number; name: string; responsibilityIds: number[] } | null>(null)

  // A move made from a role's own menu that has been accepted but whose entry
  // is still waiting for the placement the server gave the box.
  const pendingMenuMoveRef = useRef<{ seatId: number; label: string; before: SeatPlacement } | null>(null)
  // The seats as the server last sent them. `allSeatsRef` carries the
  // optimistic overlays too, and an entry must only ever hold saved values.
  const chartSeatsRef = useRef(chart.seats)
  chartSeatsRef.current = chart.seats

  const recordPendingMenuMove = useCallback((seats: SeatDetail[]) => {
    const pending = pendingMenuMoveRef.current
    if (!pending) return
    pendingMenuMoveRef.current = null
    const landed = seats.find(seat => seat.id === pending.seatId)
    // The box is no longer on the chart — someone else removed it while the
    // move was in flight — so there is nothing left to replay.
    if (!landed) return
    historyRef.current.record({
      kind: 'move',
      label: pending.label,
      seatId: pending.seatId,
      before: pending.before,
      after: placementOf(landed),
    })
  }, [])

  // The props a move's own response carries arrive on a render, which can land
  // either side of the success callback. Whichever happens last writes the
  // entry; this is the case where the render is last.
  useEffect(() => { recordPendingMenuMove(chart.seats) }, [chart.seats, recordPendingMenuMove])

  /**
   * Prepares the `onSuccess` for "Add role below", "Add role to right" and
   * "Duplicate". Nothing is written until the server has accepted the create,
   * so a refused one leaves the history alone.
   *
   * Both halves of the answer are read off the page THIS request answered
   * with — the new id from its flash, the saved row from its props — never off
   * the component's latest ones. Several creates can be queued at once, and
   * the render carrying one response's props can land after the next
   * response's callback has run: reading the current props would then record
   * the second addition against the first box's id, and undoing it would
   * delete the wrong role.
   *
   * The snapshot comes from the SERVER's copy — a duplicate arrives with
   * obsessions and tags the browser never sent — so redo rebuilds the box that
   * actually landed.
   */
  /**
   * The box a create landed, read off the page THAT request answered with —
   * never the component's latest props, which may already belong to another
   * request. Null when this response cannot be read, so nothing is guessed at.
   */
  const createdSeatFrom = useCallback((page?: InertiaPage): SeatDetail | null => {
    const props = page?.props as unknown as ChartPageProps | undefined
    const createdId = props?.flash?.created_seat_id ?? null
    const seats = props?.chart?.seats
    if (!createdId || !seats || props?.chart?.id !== chartIdentityRef.current) return null
    return seats.find(seat => seat.id === createdId) ?? null
  }, [])

  /** Opens the box a create landed, so it can be named without hunting for it. */
  const openCreatedSeat = useCallback((page?: InertiaPage) => {
    const created = createdSeatFrom(page)
    if (created) setSelectedSeat(created)
  }, [createdSeatFrom])

  const createRecorder = useCallback((label: string) => (page?: InertiaPage) => {
    // A response this page cannot read the new box out of — a router double in
    // a test, or props for a different chart — is left out of the history
    // rather than guessed at.
    const created = createdSeatFrom(page)
    const seats = (page?.props as unknown as ChartPageProps | undefined)?.chart?.seats
    if (!created || !seats) return
    historyRef.current.record({
      kind: 'create',
      label,
      seatId: created.id,
      snapshot: snapshotSeat(created, seats),
    })
  }, [createdSeatFrom])

  /**
   * A typed edit the detail panel has already had accepted. The panel reports
   * one per settled edit — a blur or a status pick, never a keystroke — and
   * only once the server holds the new value, so a refused save leaves nothing
   * behind to take back.
   */
  const recordFieldEdit = useCallback((edit: ChartFieldEdit) => {
    historyRef.current.record({ kind: 'field', ...edit })
  }, [])

  /**
   * Prepares the `onSuccess` for a move made from a role's own menu — Move up,
   * Move down, Promote, Demote, Move to. Nothing is written until the server
   * has accepted the move, so a refused one leaves the history alone.
   *
   * What the entry stores as the new placement is the one the SERVER settled
   * on, never the one the browser asked for: a hand-placed box restates its own
   * coordinates and keeps them, but an unplaced one sends none, so the move
   * endpoint places it beside its new parent — or leaves it unplaced when that
   * parent is itself laid out automatically. Recording the coordinates the box
   * had before would make redo put it back where it used to be, under a parent
   * it no longer hangs from.
   */
  const menuMoveRecorder = useCallback((moved: SeatDetail, label: string) => {
    const seatsWhenSent = chartSeatsRef.current
    return () => {
      pendingMenuMoveRef.current = { seatId: moved.id, label, before: placementOf(moved) }
      // The render carrying the response's props has already happened, so no
      // later one will: read the new placement now.
      if (chartSeatsRef.current !== seatsWhenSent) recordPendingMenuMove(chartSeatsRef.current)
    }
  }, [recordPendingMenuMove])

  const clearSelection = useCallback(() => {
    setNodes(current => (
      current.some(node => node.selected) ? current.map(node => (node.selected ? { ...node, selected: false } : node)) : current
    ))
  }, [setNodes])

  // A group is gathered deliberately — Shift+click, or a rectangle dragged from
  // empty canvas. An ordinary click on a card means "open this one", and a
  // drag means "put this one here"; neither should quietly leave the card in a
  // selection that the next alignment would act on.
  const plainCardPressRef = useRef(false)
  const notePointerDown = useCallback((event: { target: EventTarget | null; shiftKey: boolean }) => {
    const target = event.target as HTMLElement | null
    plainCardPressRef.current = !event.shiftKey && !!target?.closest?.('.react-flow__node')
  }, [])
  const noteRubberBandStart = useCallback(() => { plainCardPressRef.current = false }, [])

  const handleSelectionChange = useCallback(({ nodes: picked }: { nodes: any[] }) => {
    if (plainCardPressRef.current) {
      if (picked.length > 0) clearSelection()
      setSelectedSeatIds(current => (current.length === 0 ? current : []))
      return
    }
    const ids = picked.map(node => Number(node.id)).sort((a, b) => a - b)
    setSelectedSeatIds(current => (
      current.length === ids.length && current.every((id, index) => id === ids[index]) ? current : ids
    ))
  }, [clearSelection])

  // Nobody keeps a selection they cannot act on: switching to the Auto Layout
  // preview, or losing editing rights, drops it rather than leaving a toolbar
  // hanging over boxes that will not move.
  useEffect(() => {
    if (!canDragCards) { clearSelection(); setSelectedSeatIds([]) }
  }, [canDragCards, clearSelection])

  /** The selected boxes as they are currently DRAWN, which is what aligns. */
  const selectionBoxes = useMemo<AlignmentBox[]>(() => {
    if (selectedSeatIds.length < 2) return []
    const wanted = new Set(selectedSeatIds)
    return nodes
      .filter(node => wanted.has(Number(node.id)))
      .map(node => ({
        id: Number(node.id),
        x: node.position.x,
        y: node.position.y,
        width: node.width ?? node.measured?.width ?? CARD_WIDTH,
        height: node.height ?? node.measured?.height ?? 0,
      }))
  }, [nodes, selectedSeatIds])
  const selectionBoxesRef = useRef<AlignmentBox[]>([])
  selectionBoxesRef.current = selectionBoxes

  // ---- Connector arrows -------------------------------------------------
  // An arrow is a reporting line, so working with one is an edit and is gated
  // exactly like moving a card: a viewer, and anyone looking at the unsaved
  // Auto Layout preview, gets inert arrows rather than controls that refuse.
  const canEditConnectors = canDragCards
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const [edgeMenu, setEdgeMenu] = useState<
    { edgeId: string; parentSeatId: number; childSeatId: number; x: number; y: number } | null
  >(null)
  const [endpointDrag, setEndpointDrag] = useState<
    { edgeId: string; parentSeatId: number; childSeatId: number; point: { x: number; y: number } } | null
  >(null)
  const [newLineDrag, setNewLineDrag] = useState<
    { originSeatId: number; direction: 'top' | 'bottom'; point: { x: number; y: number } } | null
  >(null)
  /**
   * Where the loose end of the arrow is DRAWN while it is being pulled around:
   * a little behind the pointer, easing towards it, so the line reads as being
   * drawn rather than snapped taut between two pins.
   *
   * The gesture itself never consults this. Which box the pointer is over, what
   * releasing there means and where the drop lands are all resolved against the
   * real pointer position, so the outcome of a drag is exactly what it was
   * before the easing existed.
   */
  const drawnEndpoint = useEasedPoint(endpointDrag?.point ?? newLineDrag?.point ?? null)
  const edgeMenuAnchorRef = useRef<HTMLDivElement>(null)
  // The edge components are built by the layout effect and must not be rebuilt
  // every time one of these changes, so the handlers reach the latest values
  // through refs and keep a stable identity.
  const endpointDragRef = useRef<typeof endpointDrag>(null)
  const canEditConnectorsRef = useRef(canEditConnectors)
  canEditConnectorsRef.current = canEditConnectors
  const selectedEdgeIdRef = useRef<string | null>(null)
  selectedEdgeIdRef.current = selectedEdgeId
  const allSeatsRef = useRef<SeatDetail[]>([])
  allSeatsRef.current = allSeats
  const screenToFlowRef = useRef(screenToFlowPosition)
  screenToFlowRef.current = screenToFlowPosition

  /**
   * Fit the canvas the layout reports — which is padded so the top row sits at
   * its centre — rather than the cards' bounding box, and never zoom out past
   * the point where the obsession names stop being readable.
   */
  const fitChartCanvas = useCallback((canvas: { x: number; y: number; width: number; height: number }, animate: boolean) => {
    const pane = paneRef.current
    if (!pane || canvas.width <= 0 || canvas.height <= 0) return
    const paneWidth = pane.clientWidth
    const paneHeight = pane.clientHeight
    if (paneWidth <= 0 || paneHeight <= 0) return

    const usableWidth = Math.max(1, paneWidth - CHART_FIT_PADDING * 2)
    // The bottom strip belongs to the zoom buttons, the minimap and the zoom
    // readout, which float over the canvas and take the clicks aimed at
    // whatever is beneath them, so the frame stays out of it.
    const usableHeight = Math.max(1, paneHeight - CHART_FIT_PADDING * 2 - CHART_BOTTOM_CLUSTER_H)
    const zoom = Math.min(
      1,
      Math.max(MIN_LEGIBLE_ZOOM, Math.min(usableWidth / canvas.width, usableHeight / canvas.height)),
    )
    const scaledHeight = canvas.height * zoom
    // The canvas can start left of or above the origin once boxes have been
    // placed by hand, so the camera aims at its centre, not at a fixed origin.
    const topOffset = scaledHeight <= usableHeight
      ? (paneHeight - CHART_BOTTOM_CLUSTER_H - scaledHeight) / 2
      : CHART_FIT_PADDING
    setViewport(
      {
        x: paneWidth / 2 - (canvas.x + canvas.width / 2) * zoom,
        // A tree taller than the pane hangs from the top instead of being
        // centered, so the top row is always the part you land on.
        y: topOffset - canvas.y * zoom,
        zoom,
      },
      { duration: animate && !prefersReducedMotion() ? REFLOW_MS : 0 },
    )
  }, [setViewport])

  // The layout effect rebuilds every node, so it must not re-run just because
  // the fit helper was recreated: it reaches it through a ref instead.
  const fitChartCanvasRef = useRef(fitChartCanvas)
  fitChartCanvasRef.current = fitChartCanvas

  // The canvas the last layout reported, so the camera can be re-aimed when the
  // flow mounts or the pane is resized without re-running the layout.
  const canvasRef = useRef({ x: 0, y: 0, width: 0, height: 0 })
  const handleFlowInit = useCallback(() => {
    fitChartCanvasRef.current(canvasRef.current, false)
  }, [])

  // Wheel and pinch zooming, eased rather than stepped. It writes the same
  // camera `fitChartCanvas` does, and navigating is not editing: a viewer gets
  // it too.
  useSmoothWheelZoom(paneRef)

  /** Centre the canvas on a spot picked on the minimap, at the current zoom. */
  const centerOnFlowPoint = useCallback((point: { x: number; y: number }) => {
    const pane = paneRef.current
    if (!pane) return
    const { zoom } = getViewport()
    setViewport(
      { x: pane.clientWidth / 2 - point.x * zoom, y: pane.clientHeight / 2 - point.y * zoom, zoom },
      { duration: 200 },
    )
  }, [getViewport, setViewport])

  useEffect(() => {
    const pane = paneRef.current
    if (!pane || typeof ResizeObserver === 'undefined') return
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => fitChartCanvasRef.current(canvasRef.current, false))
    })
    observer.observe(pane)
    return () => { cancelAnimationFrame(frame); observer.disconnect() }
  }, [])

  // ---- Cards arriving and leaving ---------------------------------------
  //
  // The ids the last layout pass drew, so the next one can tell which cards
  // have just left the chart, and the timer that finally removes them once
  // they have finished fading.
  const renderedNodeIdsRef = useRef<Set<string>>(new Set())
  const leavingSweepRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sweepLeavingNodes = useCallback(() => {
    if (leavingSweepRef.current) clearTimeout(leavingSweepRef.current)
    leavingSweepRef.current = setTimeout(() => {
      leavingSweepRef.current = null
      setNodes(current => (current.some(node => node.data?.leaving) ? withoutLeavingNodes(current) : current))
    }, CARD_EXIT_MS)
  }, [setNodes])
  // The layout effect rebuilds every node, so it reaches the sweep through a
  // ref rather than taking it as a dependency.
  const sweepLeavingNodesRef = useRef(sweepLeavingNodes)
  sweepLeavingNodesRef.current = sweepLeavingNodes
  useEffect(() => () => { if (leavingSweepRef.current) clearTimeout(leavingSweepRef.current) }, [])

  const openSeatDetail = useCallback((seat: SeatDetail, event?: { shiftKey?: boolean }) => {
    // A drag that ends on top of the card still fires a click.
    if (Date.now() - lastDragEndRef.current < 250) return
    // Shift+click is how a card joins the selection. Opening its detail panel
    // on top of that would bury the chart the person is busy arranging.
    if (event?.shiftKey) return
    setSelectedSeat(seat)
  }, [])

  useEffect(() => {
    const { nodes: layout, edges: layoutEdges, ...canvas } = layoutChart(
      allSeats, collapsed, density, { ignoreStoredPositions: autoLayoutPreview },
    )
    layoutRef.current = layout

    const childCount = new Map<number, number>()
    allSeats.forEach(s => { if (s.parent_seat_id) childCount.set(s.parent_seat_id, (childCount.get(s.parent_seat_id) || 0) + 1) })

    const flowNodes = layout.map((n: PositionedNode) => ({
      id: n.id,
      type: 'seat',
      position: { x: n.x, y: n.y },
      width: n.width,
      height: n.height,
      data: {
        seat: n.seat,
        density,
        isCollapsed: collapsed.has(n.seat.id),
        hasChildren: (childCount.get(n.seat.id) || 0) > 0,
        dimmed: matchIds ? !matchIds.has(n.seat.id) : false,
        highlighted: matchIds ? matchIds.has(n.seat.id) : false,
        draggable: canDragCards && n.seat.can_edit,
        dropHint: null as DropHint | null,
        leaving: false,
        showNavigatorStatus: showNavigatorStatusRef.current,
        onClickSeat: openSeatDetail,
        onToggleCollapse: toggleCollapse,
        onAction: handleSeatAction,
        onConnectStart: startNewLineDrag,
      },
      // React Flow lets a node opt back into dragging even when the canvas has
      // it switched off, so the preview has to be part of the per-card answer
      // too — otherwise a card can be picked up during the preview, and the
      // movement is silently thrown away by the next layout pass.
      draggable: canDragCards && n.seat.can_edit,
      // Only a box that can be moved can be selected: the toolbar the selection
      // opens has nothing to offer for one that cannot.
      selectable: canDragCards && n.seat.can_edit,
      // This effect rebuilds every node, which would otherwise wipe the
      // selection each time a save lands and the props come back.
      selected: selectedSeatIdsRef.current.includes(n.seat.id),
    }))
    const flowEdges = layoutEdges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: 'connector',
      // Selection is the page's own state, not React Flow's: an arrow is
      // selected to be edited, never to be dragged or deleted by the canvas.
      selectable: false,
      focusable: false,
      markerEnd: { type: MarkerType.ArrowClosed, color: CONNECTOR_COLOR },
      data: {
        parentSeatId: Number(e.source),
        childSeatId: Number(e.target),
        parentName: allSeats.find(s => s.id === Number(e.source))?.name || '',
        childName: allSeats.find(s => s.id === Number(e.target))?.name || '',
        interactive: canEditConnectorsRef.current,
        selected: e.id === selectedEdgeIdRef.current,
        dragPoint: null,
        onSelect: selectConnector,
        onContextMenu: openConnectorMenu,
        onEndpointDragStart: startEndpointDrag,
        onDelete: confirmDetach,
      } as ConnectorEdgeData,
    }))
    // A branch that has just been collapsed, or a role that has just gone, is
    // held on the canvas for one short beat so it fades rather than vanishing
    // between two frames. The layout itself — and therefore every drop, every
    // save and the camera — has already forgotten it.
    const animate = !prefersReducedMotion()
    const stayingIds = new Set(flowNodes.map(n => n.id))
    const anyLeft = Array.from(renderedNodeIdsRef.current).some(id => !stayingIds.has(id))
    renderedNodeIdsRef.current = stayingIds
    setNodes(current => withLeavingNodes(current, flowNodes, animate))
    if (anyLeft && animate) sweepLeavingNodesRef.current()
    setEdges(flowEdges)

    // Any change to the node set, sizes or placement can leave the camera
    // pointing at empty canvas, so refit whenever the layout itself changed.
    canvasRef.current = canvas
    const signature = layout.map(n => `${n.id}@${Math.round(n.x)},${Math.round(n.y)}:${n.width}x${n.height}`).join('|')
    if (signature !== fitSignatureRef.current) {
      const firstFit = fitSignatureRef.current === ''
      fitSignatureRef.current = signature
      if (layout.length > 0) {
        requestAnimationFrame(() => {
          if (!mountedRef.current) return
          fitChartCanvasRef.current(canvas, !firstFit)
        })
      }
    }
  }, [allSeats, autoLayoutPreview, collapsed, density, matchIds, openSeatDetail])

  // Flipping the toggle only patches a flag into the existing nodes: the
  // layout is not re-run, so positions, sizes, collapse state and selection all
  // stay exactly as they were.
  useEffect(() => {
    setNodes(current => current.map(n => (
      n.data?.showNavigatorStatus === showNavigatorStatus
        ? n
        : { ...n, data: { ...n.data, showNavigatorStatus } }
    )))
  }, [showNavigatorStatus, setNodes])

  // Drop hints are painted without re-running the layout so the card being
  // dragged keeps following the pointer.
  useEffect(() => {
    setNodes(current => current.map(node => {
      const hint = dropTarget && String(dropTarget.anchorSeatId) === node.id ? dropTarget.hint : null
      if (node.data.dropHint === hint) return node
      return { ...node, data: { ...node.data, dropHint: hint } }
    }))
  }, [dropTarget, setNodes])

  /**
   * A refused drop puts the card back where the gesture STARTED, not where the
   * tidy layout would put it: the card may well have been hand-placed, and
   * snapping it to a generated slot would silently throw that placement away.
   */
  const returnToDragStart = useCallback(() => {
    const start = dragStartRef.current
    setNodes(current => current.map(node => {
      if (start && node.id === start.id) return { ...node, dragging: false, position: { x: start.x, y: start.y } }
      const placed = layoutRef.current.find(n => n.id === node.id)
      if (!placed || (node.position.x === placed.x && node.position.y === placed.y)) return node
      return { ...node, dragging: false, position: { x: placed.x, y: placed.y } }
    }))
  }, [setNodes])

  /** Paint the card at the spot the drop resolved to, snapping included. */
  const placeNodeAt = useCallback((seatId: number, position: { x: number; y: number }) => {
    setNodes(current => current.map(node => (
      node.id === String(seatId) ? { ...node, dragging: false, position: { ...position } } : node
    )))
  }, [setNodes])

  // ---- Saving where boxes sit -------------------------------------------

  const round2 = (value: number) => Math.round(value * 100) / 100

  /** What the chart has STORED for these boxes — null where none was ever set. */
  const storedCoordinates = useCallback((ids: number[]): SeatCoordinates[] => ids.map(id => {
    const seat = allSeatsRef.current.find(s => s.id === id)
    return seat ? coordinatesOf(seat) : { id, x: null, y: null }
  }), [])

  /**
   * One request for however many boxes moved. A null coordinate pair means
   * "unplaced", which is how undoing a first-ever move — or an Auto Layout
   * apply — hands a box back to the tidy tree.
   */
  const submitPositions = useCallback((options: {
    key: string
    positions: SeatCoordinates[]
    fallback: string
    onSuccess?: () => void
    onError?: (errors: unknown) => void
    onCancel?: () => void
    onFinish?: () => void
  }) => {
    const optimistic: Record<number, { x: number; y: number }> = {}
    options.positions.forEach(p => { if (p.x !== null && p.y !== null) optimistic[p.id] = { x: p.x, y: p.y } })
    setPendingPositions(prev => ({ ...prev, ...optimistic }))
    const release = () => options.positions.forEach(p => clearPendingPosition(p.id))
    const accepted = enqueueChartMutation({
      key: options.key,
      url: `${basePath}/seats/positions`,
      method: 'post',
      data: { positions: options.positions.map(p => ({ id: p.id, x: p.x ?? '', y: p.y ?? '' })) },
      singleFlight: true,
      onSuccess: options.onSuccess,
      onError: options.onError,
      // Only when the caller asked for it: without one the queue's own notice
      // is the right answer, and replacing it with silence would hide the loss.
      onCancel: options.onCancel,
      // Dropping the optimistic placement is what puts a refused change back on
      // screen: the next layout pass draws the coordinates the server still has.
      onFinish: () => { release(); options.onFinish?.() },
    }, options.fallback)
    if (!accepted) { release(); options.onFinish?.() }
    return accepted
  }, [basePath, clearPendingPosition, enqueueChartMutation])

  // ---- Moving a whole selection -----------------------------------------
  //
  // A group drag asks no questions about reporting lines: several boxes cannot
  // all land on one parent, and a selection is a tidying gesture rather than a
  // restructuring one. Every box keeps its parent and only its coordinates move.
  const groupDragRef = useRef<{ ids: number[]; before: SeatCoordinates[] } | null>(null)
  /** The stored coordinates a single-card drag started from, for undo. */
  const dragStartCoordsRef = useRef<SeatCoordinates | null>(null)

  /**
   * The box whose position decides a group's snap: the top-left-most of the
   * ones actually moving. The indication drawn mid-drag and the correction
   * applied on release both go through here, so the line picked out under a
   * selection is the line that selection lands on.
   */
  const groupLead = (moving: any[]): any | null => (moving.length === 0 ? null : moving.reduce((best, n) => (
    n.position.y < best.position.y || (n.position.y === best.position.y && n.position.x < best.position.x) ? n : best
  )))

  /** The boxes of `dragged` that the group drag in progress is carrying. */
  const groupMembers = (dragged: any[] | undefined): any[] => {
    const group = groupDragRef.current
    const nodes = dragged ?? []
    return group ? nodes.filter(n => group.ids.includes(Number(n.id))) : nodes
  }

  const beginGroupDrag = useCallback((dragged: any[] | undefined) => {
    const ids = (dragged ?? []).filter(n => n.draggable !== false).map(n => Number(n.id))
    groupDragRef.current = ids.length > 1 ? { ids, before: storedCoordinates(ids) } : null
    return groupDragRef.current !== null
  }, [storedCoordinates])

  const finishGroupDrag = useCallback((dragged: any[] | undefined) => {
    if (cancelledDragRef.current) { returnToDragStart(); return }
    const group = groupDragRef.current
    groupDragRef.current = null
    setDropTarget(null)
    setDraggingSeatId(null)
    setSnapLines(null)
    lastDragEndRef.current = Date.now()
    if (!group) return

    const moving = (dragged ?? []).filter(n => group.ids.includes(Number(n.id)))
    if (moving.length === 0) return

    // The selection snaps as one shape: the lead box is eased onto a guideline
    // and every other box moves by the same amount, so the arrangement inside
    // the selection survives the gesture untouched.
    const lead = groupLead(moving)!
    const snapped = snapPlacement(lead, moving).position
    snapRef.current = null
    const dx = snapped.x - lead.position.x
    const dy = snapped.y - lead.position.y

    const after: SeatCoordinates[] = moving.map(n => ({
      id: Number(n.id),
      x: round2(n.position.x + dx),
      y: round2(n.position.y + dy),
    }))
    const before = group.before.filter(b => after.some(a => a.id === b.id))
    after.forEach(p => placeNodeAt(p.id, { x: p.x as number, y: p.y as number }))

    const label = `move ${after.length} ${LABELS.seats.toLowerCase()}`
    const accepted = submitPositions({
      key: 'chart:positions',
      positions: after,
      fallback: `Unable to save where you placed those ${LABELS.seats.toLowerCase()}.`,
      onSuccess: () => historyRef.current.record({ kind: 'positions', label, before, after }),
    })
    if (!accepted) setEditError('Another placement is still saving. Try again in a moment.')
  }, [placeNodeAt, setEditError, submitPositions, snapPlacement])

  /**
   * Tidy the selection. Every selected box is written, not only the ones that
   * shift: aligning says "these belong on this line", and a box the tidy tree
   * happened to already put there would otherwise drift off it the next time
   * something above it changed.
   */
  const applySelectionAlignment = useCallback((action: AlignmentAction) => {
    if (!canDragCards || replayingRef.current) return
    const boxes = selectionBoxesRef.current
    const results = applyAlignment(boxes, action)
    if (results.length === 0) {
      setDropNotice(`Select at least three ${LABELS.seats.toLowerCase()} to ${ALIGNMENT_LABELS[action]}.`)
      return
    }
    const before = storedCoordinates(results.map(r => r.id))
    const after: SeatCoordinates[] = results.map(r => ({ id: r.id, x: r.x, y: r.y }))
    const settled = before.every(b => {
      const next = after.find(a => a.id === b.id)!
      return b.x !== null && b.y !== null && Math.abs(b.x - (next.x as number)) < 0.5 && Math.abs(b.y - (next.y as number)) < 0.5
    })
    if (settled) {
      setDropNotice(`Those ${LABELS.seats.toLowerCase()} are already where ${ALIGNMENT_LABELS[action]} would put them.`)
      return
    }

    setEditError(null)
    setAligning(true)
    after.forEach(p => placeNodeAt(p.id, { x: p.x as number, y: p.y as number }))
    const accepted = submitPositions({
      key: 'chart:positions',
      positions: after,
      fallback: `Unable to ${ALIGNMENT_LABELS[action]}.`,
      onSuccess: () => {
        historyRef.current.record({ kind: 'positions', label: ALIGNMENT_LABELS[action], before, after })
        setDropNotice(`Applied ${ALIGNMENT_LABELS[action]} to ${after.length} ${LABELS.seats.toLowerCase()}.`)
      },
      onFinish: () => setAligning(false),
    })
    if (!accepted) setEditError('Another placement is still saving. Try again in a moment.')
  }, [canDragCards, placeNodeAt, setDropNotice, setEditError, storedCoordinates, submitPositions])

  /** The card the dragged card is over, and what dropping there would mean. */
  const resolveDropTarget = useCallback((event: any, node: any) => {
    const placed = layoutRef.current.find(n => n.id === String(node.id))
    const width = node.width ?? node.measured?.width ?? placed?.width ?? CARD_WIDTH
    const height = node.height ?? node.measured?.height ?? placed?.height ?? 0
    const card = { x: node.position.x, y: node.position.y, width, height }
    const hasPointer = event && typeof event.clientX === 'number' && typeof event.clientY === 'number'
    return resolveDrop({
      nodes: layoutRef.current,
      seats: allSeats,
      draggedSeatId: Number(node.id),
      // The whole card is the thing being aimed, and the pointer says which row
      // it is aimed at — a card grabbed by its header hangs below that row.
      card,
      pointer: hasPointer
        ? screenToFlowPosition({ x: event.clientX, y: event.clientY })
        : { x: card.x + width / 2, y: card.y + height / 2 },
      labels: { seat: LABELS.seat.toLowerCase(), seats: LABELS.seats.toLowerCase() },
    })
  }, [allSeats, screenToFlowPosition])

  const onNodeDragStart = useCallback((_event: any, node: any, dragged?: any[]) => {
    cancelledDragRef.current = false
    setEditError(null)
    // Picking up a card that is part of a selection carries the whole selection.
    if (beginGroupDrag(dragged)) {
      dragStartRef.current = null
      dragStartCoordsRef.current = null
    } else {
      dragStartRef.current = { id: String(node.id), x: node.position.x, y: node.position.y }
      dragStartCoordsRef.current = storedCoordinates([Number(node.id)])[0]
    }
    setDraggingSeatId(Number(node.id))
    // Nothing has moved yet, so nothing is promised yet.
    setSnapLines(null)
    snapRef.current = null
  }, [beginGroupDrag, storedCoordinates])

  /**
   * What the grid is promising right now.
   *
   * Only open canvas gets an indication: a release on another card is placed
   * by that card, and a refused one goes back where it came from, so in both
   * of those the grid has nothing to promise and must say nothing.
   */
  const indicateSnap = useCallback((node: any, target: DropResolution | null, moving?: any[]) => {
    const placedByCard = target !== null
      && (target.hint === 'invalid' || (target.hint === 'child' && target.parentSeatId !== null))
    snapRef.current = placedByCard ? null : snapPlacement(node, moving)
    setSnapLines(snapRef.current?.guides ?? null)
  }, [snapPlacement])

  const onNodeDrag = useCallback((event: any, node: any, dragged?: any[]) => {
    if (cancelledDragRef.current) return
    // A group has no drop target: it is a placement gesture, never a re-parent.
    if (groupDragRef.current) {
      setDropTarget(null)
      const lead = groupLead(groupMembers(dragged))
      indicateSnap(lead ?? node, null, groupMembers(dragged))
      return
    }
    const target = resolveDropTarget(event, node)
    setDropTarget(target)
    indicateSnap(node, target)
  }, [indicateSnap, resolveDropTarget])

  const onSelectionDragStart = useCallback((_event: any, dragged: any[]) => {
    cancelledDragRef.current = false
    setEditError(null)
    beginGroupDrag(dragged)
    setDraggingSeatId(Number(groupLead(dragged)?.id) || null)
    snapRef.current = null
    setSnapLines(null)
  }, [beginGroupDrag])

  /** Dragging the selection rectangle itself: same lead box, same indication. */
  const onSelectionDrag = useCallback((_event: any, dragged: any[]) => {
    if (cancelledDragRef.current) return
    const lead = groupLead(groupMembers(dragged))
    if (!lead) { setSnapLines(null); return }
    indicateSnap(lead, null, groupMembers(dragged))
  }, [indicateSnap])

  const onSelectionDragStop = useCallback((_event: any, dragged: any[]) => {
    finishGroupDrag(dragged)
  }, [finishGroupDrag])

  /**
   * A release means one of two things, and both of them keep the spot the card
   * was let go at: on top of another card it also becomes that card's report,
   * and anywhere else it simply stays there.
   */
  const onNodeDragStop = useCallback((event: any, node: any, dragged?: any[]) => {
    if (cancelledDragRef.current) { returnToDragStart(); return }
    if (groupDragRef.current) { finishGroupDrag(dragged ?? [node]); return }
    const seatId = Number(node.id)
    const target = resolveDropTarget(event, node)
    setDropTarget(null)
    setDraggingSeatId(null)
    setSnapLines(null)
    lastDragEndRef.current = Date.now()

    const moved = allSeats.find(s => s.id === seatId)
    if (!moved) { returnToDragStart(); dragStartRef.current = null; return }

    if (target && target.hint === 'invalid') {
      setEditError(target.reason || `Unable to move this ${LABELS.seat.toLowerCase()} there.`)
      returnToDragStart()
      dragStartRef.current = null
      return
    }

    // Landing on a card and changing who a card reports to are two different
    // questions. A card dropped back on the role it already reports to still
    // has to come to rest under it rather than covering it, so the placement
    // follows the card that was aimed at, and only the reporting line depends
    // on that card being a new parent.
    const onCard = target && target.hint === 'child' && target.parentSeatId !== null ? target : null
    const reparent = onCard && onCard.parentSeatId !== moved.parent_seat_id ? onCard : null
    // Guidelines are an aid, not a rule: a release near a line eases onto it,
    // one away from every line keeps its exact spot.
    const targetNode = onCard ? layoutRef.current.find(n => n.id === String(onCard.parentSeatId)) : undefined
    const dropped = targetNode
      ? placeUnderTarget(targetNode, layoutRef.current, seatId)
      : snapPlacement(node).position
    snapRef.current = null

    const failed = (message: string) => {
      setPendingMove(null)
      clearPendingPosition(seatId)
      returnToDragStart()
      dragStartRef.current = null
      setEditError(message)
    }

    const startedAt = dragStartCoordsRef.current ?? { id: seatId, x: moved.layout_x, y: moved.layout_y }
    const landedAt: SeatCoordinates = { id: seatId, x: dropped.x, y: dropped.y }
    const movedLabel = `move "${moved.name}"`

    setPendingPositions(prev => ({ ...prev, [seatId]: dropped }))
    placeNodeAt(seatId, dropped)

    if (reparent) {
      // The new parent and the new coordinates travel together, so a refused
      // move cannot leave the box saved at a spot it was never allowed to take.
      setPendingMove({ seatId, parentSeatId: reparent.parentSeatId, position: reparent.position })
      const accepted = enqueueChartMutation({
        key: `seat:${seatId}:move`,
        url: `${basePath}/seats/${seatId}/move`,
        method: 'post',
        data: {
          parent_seat_id: reparent.parentSeatId ?? '',
          position: reparent.position,
          layout_x: dropped.x,
          layout_y: dropped.y,
        },
        singleFlight: true,
        onSuccess: () => historyRef.current.record({
          kind: 'move',
          label: movedLabel,
          seatId,
          before: { parentSeatId: moved.parent_seat_id, position: moved.position, x: startedAt.x, y: startedAt.y },
          after: { parentSeatId: reparent.parentSeatId, position: reparent.position, x: dropped.x, y: dropped.y },
        }),
        onFinish: () => { setPendingMove(null); clearPendingPosition(seatId) },
      }, `Unable to move this ${LABELS.seat.toLowerCase()}.`)
      if (!accepted) failed(`Another change to this ${LABELS.seat.toLowerCase()} is still saving. Try again in a moment.`)
      else dragStartRef.current = null
      return
    }

    const accepted = enqueueChartMutation({
      key: `seat:${seatId}:position`,
      url: `${basePath}/seats/positions`,
      method: 'post',
      data: { positions: [{ id: seatId, x: dropped.x, y: dropped.y }] },
      singleFlight: true,
      onSuccess: () => historyRef.current.record({
        kind: 'positions', label: movedLabel, before: [startedAt], after: [landedAt],
      }),
      onFinish: () => clearPendingPosition(seatId),
    }, `Unable to save where you placed this ${LABELS.seat.toLowerCase()}.`)
    if (!accepted) failed(`Another change to this ${LABELS.seat.toLowerCase()} is still saving. Try again in a moment.`)
    else dragStartRef.current = null
  }, [allSeats, basePath, clearPendingPosition, enqueueChartMutation, placeNodeAt, resolveDropTarget, returnToDragStart, snapPlacement])

  useEffect(() => {
    const cancel = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return
      if (draggingSeatId === null && !groupDragRef.current) return
      cancelledDragRef.current = true
      returnToDragStart()
      groupDragRef.current = null
      setDraggingSeatId(null)
      setDropTarget(null)
      setSnapLines(null)
      snapRef.current = null
    }
    window.addEventListener('keydown', cancel)
    window.addEventListener('pointercancel', cancel)
    window.addEventListener('blur', cancel)
    return () => {
      window.removeEventListener('keydown', cancel)
      window.removeEventListener('pointercancel', cancel)
      window.removeEventListener('blur', cancel)
    }
  }, [draggingSeatId, returnToDragStart])

  // ---- Working with an arrow -------------------------------------------
  //
  // Every arrow change is a change to who reports to whom, so all three
  // gestures — redirect, detach and delete — go through the same seat move
  // endpoint (and therefore the same authorization) that dragging a card uses.
  // A refusal is shown inline and the optimistic move is dropped, which puts
  // the arrow back exactly where it was.

  const clearConnectorGesture = useCallback(() => {
    endpointDragRef.current = null
    setEndpointDrag(null)
    setNewLineDrag(null)
    setDropTarget(null)
  }, [])

  /**
   * Send an arrow change. The child's stored coordinates travel with it so a
   * re-parent is a change to the reporting line ONLY: without them the server
   * re-places a re-parented box under its new parent, silently throwing away a
   * placement someone made by hand.
   */
  const submitConnectorMove = useCallback((childSeatId: number, parentSeatId: number | null, position: number, newLine = false) => {
    const child = allSeatsRef.current.find(s => s.id === childSeatId)
    if (!child) return
    if (!child.can_edit) {
      setEditError(`You don't have permission to change where "${child.name}" reports.`)
      return
    }
    setEditError(null)
    setDropNotice(null)

    // An arrow changes who reports to whom and nothing else, so the box has to
    // stay exactly where it is on screen. The move endpoint re-places a
    // re-parented box whenever no coordinates come with the request, and most
    // boxes are unplaced (the tidy layout decides where they sit), so the spot
    // the box is drawn at right now is sent when there is no stored one.
    const drawn = layoutRef.current.find(n => n.seat.id === childSeatId)
    const x = child.layout_x ?? drawn?.x
    const y = child.layout_y ?? drawn?.y
    const data: Record<string, unknown> = { parent_seat_id: parentSeatId ?? '', position }
    if (x !== undefined && y !== undefined) {
      data.layout_x = x
      data.layout_y = y
    }

    const before: SeatPlacement = placementOf(child)
    const after: SeatPlacement = {
      parentSeatId,
      position,
      // Undo has to send coordinates too, so it records what was actually sent
      // rather than what was stored: an unplaced box that an arrow change has
      // just pinned down is pinned at the spot it was drawn at.
      x: x ?? null,
      y: y ?? null,
    }

    setPendingMove({ seatId: childSeatId, parentSeatId, position })
    const accepted = enqueueChartMutation({
      key: `seat:${childSeatId}:move`,
      url: `${basePath}/seats/${childSeatId}/move`,
      method: 'post',
      data,
      singleFlight: true,
      onSuccess: () => historyRef.current.record({
        kind: 'move',
        label: parentSeatId === null
          ? `detach "${child.name}"`
          : `point "${child.name}" at a new ${LABELS.seat.toLowerCase()}`,
        seatId: childSeatId,
        before,
        after,
      }),
      onFinish: () => setPendingMove(null),
    }, `Unable to change where "${child.name}" reports.`)
    if (!accepted) {
      setPendingMove(null)
      setEditError(`Another change to this ${LABELS.seat.toLowerCase()} is still saving. Try again in a moment.`)
    } else if (newLine && parentSeatId !== null && child.parent_seat_id !== null) {
      const previous = allSeatsRef.current.find(seat => seat.id === child.parent_seat_id)
      const next = allSeatsRef.current.find(seat => seat.id === parentSeatId)
      if (previous && next) {
        setDropNotice(`"${child.name}" now reports to "${next.name}" instead of "${previous.name}". Undo reverses this change.`)
      }
    }
  }, [basePath, enqueueChartMutation, setDropNotice, setEditError])

  /**
   * Detaching is the one arrow change that cannot be seen coming, so it is
   * spelled out before it happens: the box — and everything under it — ends up
   * at the top level of the chart.
   */
  const confirmDetach = useCallback((childSeatId: number) => {
    const seats = allSeatsRef.current
    const child = seats.find(s => s.id === childSeatId)
    if (!child) return
    const parent = seats.find(s => s.id === child.parent_seat_id)
    const reports = branchSize(seats, childSeatId) - 1
    const position = siblingsOf(seats, null).filter(s => s.id !== childSeatId).length
    const branch = reports === 0
      ? ''
      : ` Its ${reports} ${reports === 1 ? LABELS.seat.toLowerCase() : LABELS.seats.toLowerCase()} below it move with it.`
    setConfirmModal({
      title: 'Remove this reporting line?',
      variant: 'warning',
      message: parent
        ? `"${child.name}" will no longer report to "${parent.name}" and becomes a top-level ${LABELS.seat.toLowerCase()}.${branch}`
        : `"${child.name}" becomes a top-level ${LABELS.seat.toLowerCase()}.${branch}`,
      onConfirm: () => {
        setConfirmModal(null)
        setSelectedEdgeId(null)
        submitConnectorMove(childSeatId, null, position)
      },
    })
  }, [submitConnectorMove])

  const applyConnectorResolution = useCallback((childSeatId: number, resolution: ConnectorResolution, newLine = false) => {
    switch (resolution.outcome) {
      case 'reparent':
        setSelectedEdgeId(null)
        submitConnectorMove(childSeatId, resolution.parentSeatId, resolution.position, newLine)
        break
      case 'detach':
        confirmDetach(childSeatId)
        break
      case 'noop':
        setDropNotice(resolution.reason || null)
        break
      case 'cancel':
        break
      default:
        setEditError(resolution.reason || `Unable to point this arrow there.`)
    }
  }, [confirmDetach, setDropNotice, setEditError, submitConnectorMove])

  const selectConnector = useCallback((edgeId: string) => {
    if (!canEditConnectorsRef.current) return
    setEditError(null)
    setSelectedEdgeId(edgeId)
  }, [setEditError])

  const openConnectorMenu = useCallback((payload: {
    edgeId: string; parentSeatId: number; childSeatId: number; clientX: number; clientY: number
  }) => {
    if (!canEditConnectorsRef.current) return
    setEditError(null)
    setSelectedEdgeId(payload.edgeId)
    setEdgeMenu({
      edgeId: payload.edgeId,
      parentSeatId: payload.parentSeatId,
      childSeatId: payload.childSeatId,
      x: payload.clientX,
      y: payload.clientY,
    })
  }, [setEditError])

  const startEndpointDrag = useCallback((payload: {
    edgeId: string; parentSeatId: number; childSeatId: number; clientX: number; clientY: number
  }) => {
    if (!canEditConnectorsRef.current) return
    setEditError(null)
    setDropNotice(null)
    setEdgeMenu(null)
    setSelectedEdgeId(payload.edgeId)
    const drag = {
      edgeId: payload.edgeId,
      parentSeatId: payload.parentSeatId,
      childSeatId: payload.childSeatId,
      point: screenToFlowRef.current({ x: payload.clientX, y: payload.clientY }),
    }
    endpointDragRef.current = drag
    setEndpointDrag(drag)
  }, [setDropNotice, setEditError])

  const startNewLineDrag = useCallback((originSeatId: number, direction: 'top' | 'bottom', clientX: number, clientY: number) => {
    if (!canEditConnectorsRef.current || !allSeatsRef.current.find(seat => seat.id === originSeatId)?.can_edit) return
    setEditError(null)
    setDropNotice(null)
    setEdgeMenu(null)
    setSelectedEdgeId(null)
    setNewLineDrag({
      originSeatId, direction,
      point: screenToFlowRef.current({ x: clientX, y: clientY }),
    })
  }, [setEditError, setDropNotice])

  /**
   * The gesture is followed on the window, not the canvas: an endpoint dragged
   * off the pane, or released over a card, must still end the drag.
   */
  useEffect(() => {
    if (!endpointDrag && !newLineDrag) return

    const resolveAt = (event: PointerEvent) => {
      const point = screenToFlowRef.current({ x: event.clientX, y: event.clientY })
      return {
        point,
        resolution: newLineDrag ? resolveNewConnectorEndpoint({
          nodes: layoutRef.current,
          seats: allSeatsRef.current,
          originSeatId: newLineDrag.originSeatId,
          direction: newLineDrag.direction,
          pointer: point,
          labels: DROP_LABELS,
        }) : resolveConnectorEndpoint({
          nodes: layoutRef.current,
          seats: allSeatsRef.current,
          childSeatId: endpointDrag!.childSeatId,
          pointer: point,
          labels: DROP_LABELS,
        }),
      }
    }

    const onMove = (event: PointerEvent) => {
      const { point, resolution } = resolveAt(event)
      setEndpointDrag(current => (current ? { ...current, point } : current))
      setNewLineDrag(current => (current ? { ...current, point } : current))
      setDropTarget(resolution.anchorSeatId !== null && resolution.hint
        ? {
          anchorSeatId: resolution.anchorSeatId,
          hint: resolution.hint,
          parentSeatId: resolution.parentSeatId,
          position: resolution.position,
          reason: resolution.reason,
        }
        : null)
    }

    const onUp = (event: PointerEvent) => {
      const { resolution } = resolveAt(event)
      clearConnectorGesture()
      lastDragEndRef.current = Date.now()
      const childSeatId = newLineDrag ? resolveNewConnectorEndpoint({
        nodes: layoutRef.current, seats: allSeatsRef.current,
        originSeatId: newLineDrag.originSeatId, direction: newLineDrag.direction,
        pointer: screenToFlowRef.current({ x: event.clientX, y: event.clientY }), labels: DROP_LABELS,
      }).childSeatId : endpointDrag!.childSeatId
      if (childSeatId !== null) applyConnectorResolution(childSeatId, resolution, !!newLineDrag)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearConnectorGesture()
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [applyConnectorResolution, clearConnectorGesture, endpointDrag?.edgeId, newLineDrag?.originSeatId, newLineDrag?.direction])

  /**
   * Selection, the live endpoint and the read-only switch are patched into the
   * existing edges: re-running the layout for them would fight the gesture.
   */
  useEffect(() => {
    setEdges(current => current.map(edge => {
      const selected = canEditConnectors && edge.id === selectedEdgeId
      const dragPoint = endpointDrag && endpointDrag.edgeId === edge.id ? drawnEndpoint : null
      const data = edge.data as ConnectorEdgeData
      // Cards are painted over the arrows, and an endpoint handle sits on the
      // edge of a card: without lifting the selected arrow above the cards,
      // half of the handle is buried and the grab lands on the card instead.
      const zIndex = selected ? 1001 : 0
      if (data.selected === selected && data.interactive === canEditConnectors &&
        data.dragPoint === dragPoint && edge.zIndex === zIndex) return edge
      return { ...edge, zIndex, data: { ...data, selected, interactive: canEditConnectors, dragPoint } }
    }))
  }, [canEditConnectors, drawnEndpoint, endpointDrag, selectedEdgeId, setEdges])

  /** Nothing about an arrow survives losing the right to edit it. */
  useEffect(() => {
    if (canEditConnectors) return
    setSelectedEdgeId(null)
    setEdgeMenu(null)
    clearConnectorGesture()
  }, [canEditConnectors, clearConnectorGesture])

  const deselectConnector = useCallback(() => {
    setSelectedEdgeId(null)
    setEdgeMenu(null)
  }, [])

  // ---- Undo and redo -----------------------------------------------------
  //
  // A replay is the original action again, in reverse or forwards: same URL,
  // same payload shape, same policy check. Nothing is rewritten locally and
  // then reconciled — the entry only moves between the two stacks once the
  // server has accepted it, so a refusal leaves both the chart and the history
  // exactly as they were and says why.

  const replayEntry = useCallback((entry: ChartHistoryEntry, direction: 'undo' | 'redo') => {
    const undoing = direction === 'undo'
    setReplaying(true)
    setEditError(null)

    const settled = () => {
      setReplaying(false)
      if (undoing) historyRef.current.commitUndo(entry)
      else historyRef.current.commitRedo(entry)
      setDropNotice(`${undoing ? 'Undid' : 'Redid'} ${entry.label}.`)
    }
    const refused = (errors: unknown) => {
      setReplaying(false)
      setEditError(inertiaErrorMessage(
        errors,
        `Unable to ${direction} ${entry.label}. The chart has been left as it was.`,
      ))
    }
    const busy = () => {
      setReplaying(false)
      setEditError('Another change is still saving. Try again in a moment.')
    }
    // A replay interrupted by navigation never reached the server, so the entry
    // stays exactly where it was — but the buttons have to come back, or undo
    // is dead for the rest of the visit.
    const interrupted = () => {
      setReplaying(false)
      setEditError(`Unable to ${direction} ${entry.label}: the change was interrupted by navigation.`)
    }

    if (entry.kind === 'positions') {
      const target = undoing ? entry.before : entry.after
      if (!submitPositions({
        key: 'chart:positions',
        positions: target,
        fallback: `Unable to ${direction} ${entry.label}.`,
        onSuccess: settled,
        onError: refused,
        onCancel: interrupted,
      })) busy()
      return
    }

    if (entry.kind === 'move') {
      const target = undoing ? entry.before : entry.after
      setPendingMove({ seatId: entry.seatId, parentSeatId: target.parentSeatId, position: target.position })
      const accepted = enqueueChartMutation({
        key: `seat:${entry.seatId}:move`,
        url: `${basePath}/seats/${entry.seatId}/move`,
        method: 'post',
        data: {
          parent_seat_id: target.parentSeatId ?? '',
          position: target.position,
          // The reporting line and the coordinates travel together, as they do
          // for a drag: without them the server re-places the box and "undo"
          // would leave it somewhere it has never been.
          layout_x: target.x ?? '',
          layout_y: target.y ?? '',
        },
        singleFlight: true,
        onSuccess: settled,
        onError: refused,
        onCancel: interrupted,
        onFinish: () => setPendingMove(null),
      })
      if (!accepted) { setPendingMove(null); busy() }
      return
    }

    // A typed edit is replayed as the same save again with the other value, so
    // it travels the endpoint — and the policy — the original edit used.
    if (entry.kind === 'field') {
      const value = undoing ? entry.before : entry.after
      const target = entry.target
      const seat = target.type === 'seat'
      const accepted = enqueueChartMutation({
        key: seat
          ? `seat:${target.seatId}:header`
          : `responsibility:${target.responsibilityId}:update`,
        url: seat
          ? `${basePath}/seats/${target.seatId}`
          : `${basePath}/seats/${target.seatId}/responsibilities/${target.responsibilityId}`,
        method: 'patch',
        // Only the field the entry is about is sent: anything else about the
        // box may have moved on since, and a replay must not put that back too.
        data: seat
          ? { seat: { [target.field]: value } }
          : { responsibility: { title: value } },
        singleFlight: true,
        onSuccess: settled,
        onError: refused,
        onCancel: interrupted,
      })
      if (!accepted) busy()
      return
    }

    // A deletion and a creation are the same two requests in opposite orders:
    // one takes the box away, the other builds it back from the snapshot.
    // Taking back an addition deletes the box through the same endpoint the
    // menu's own Delete uses, and putting it back goes through restore.
    const rebuilding = entry.kind === 'delete' ? undoing : !undoing

    if (!rebuilding) {
      const accepted = enqueueChartMutation({
        key: `seat:${entry.seatId}:delete`,
        url: `${basePath}/seats/${entry.seatId}`,
        method: 'delete',
        singleFlight: true,
        onSuccess: settled,
        onError: refused,
        onCancel: interrupted,
      })
      if (!accepted) busy()
      return
    }

    const snapshot = entry.snapshot
    // The rebuilt box gets a new id, so every entry that names the old one has
    // to be told about it. `restored_seat_id` comes back in the flash.
    awaitingRestoreRef.current = {
      seatId: entry.seatId,
      name: snapshot.name,
      // Its responsibilities are rebuilt as new rows too, so the ids they had
      // are carried here to be renumbered onto them.
      responsibilityIds: snapshot.responsibilityIds,
    }
    const accepted = enqueueChartMutation({
      key: 'chart:seat-restore',
      url: `${basePath}/seats/restore`,
      method: 'post',
      data: {
        seat: {
          name: snapshot.name,
          description: snapshot.description ?? '',
          seat_status: snapshot.seatStatus,
          parent_seat_id: snapshot.placement.parentSeatId ?? '',
          position: snapshot.placement.position,
          layout_x: snapshot.placement.x ?? '',
          layout_y: snapshot.placement.y ?? '',
        },
        responsibilities: snapshot.responsibilities,
        tag_ids: snapshot.tagIds,
        assignments: snapshot.assignments,
        child_seat_ids: snapshot.childSeatIds,
        // The box's comments were detached when it went, not destroyed. This
        // is the id they are waiting under, so the server can put them back on
        // the row it rebuilds.
        deleted_seat_id: entry.seatId,
      },
      singleFlight: true,
      onSuccess: settled,
      onError: errors => { awaitingRestoreRef.current = null; refused(errors) },
      onCancel: () => { awaitingRestoreRef.current = null; interrupted() },
    })
    if (!accepted) { awaitingRestoreRef.current = null; busy() }
  }, [basePath, enqueueChartMutation, setDropNotice, setEditError, submitPositions])

  useEffect(() => {
    const awaiting = awaitingRestoreRef.current
    if (awaiting === null || !restoredSeatId) return
    awaitingRestoreRef.current = null
    // The rebuilt responsibilities come back in the order they were saved in,
    // so they pair off against the ids the old rows had. Anything that does not
    // pair — a row the server did not rebuild — is left unmapped, and the
    // history drops the entries naming it rather than replaying them at a row
    // that no longer exists.
    const rebuilt = [...(chart.seats.find(seat => seat.id === restoredSeatId)?.responsibilities ?? [])]
      .sort((a, b) => a.position - b.position)
    const responsibilityIds: Record<number, number> = {}
    if (rebuilt.length === awaiting.responsibilityIds.length) {
      awaiting.responsibilityIds.forEach((oldId, index) => { responsibilityIds[oldId] = rebuilt[index].id })
    }
    historyRef.current.remapSeatId(awaiting.seatId, restoredSeatId, responsibilityIds)
    // The conversation comes back on the rebuilt box, with the people and the
    // times it was written at. Saying how much of it returned is how the
    // person who undid the deletion knows it did.
    if (restoredCommentCount > 0) {
      setDropNotice(
        `Put "${awaiting.name}" back with its ${restoredCommentCount} ${restoredCommentCount === 1 ? 'comment' : 'comments'}.`,
      )
    }
  }, [chart.seats, restoredSeatId, restoredCommentCount, setDropNotice])

  const undo = useCallback(() => {
    if (!canManage || replayingRef.current) return
    // The toolbar stays on screen during the tidy-tree preview, so pressing it
    // there says why nothing happened instead of doing nothing at all.
    if (autoLayoutPreview) { setEditError(AUTO_LAYOUT_BLOCKED); return }
    const entry = historyRef.current.peekUndo()
    if (entry) replayEntry(entry, 'undo')
  }, [autoLayoutPreview, canManage, replayEntry, setEditError])

  const redo = useCallback(() => {
    if (!canManage || replayingRef.current) return
    if (autoLayoutPreview) { setEditError(AUTO_LAYOUT_BLOCKED); return }
    const entry = historyRef.current.peekRedo()
    if (entry) replayEntry(entry, 'redo')
  }, [autoLayoutPreview, canManage, replayEntry, setEditError])

  useEffect(() => {
    if (!canDragCards) return
    const isTyping = (target: EventTarget | null) => {
      const el = target as HTMLElement | null
      if (!el || typeof el.tagName !== 'string') return false
      return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { clearSelection(); return }
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return
      const key = event.key.toLowerCase()
      if (key !== 'z' && key !== 'y') return
      // Somebody renaming the chart or typing in the detail panel means Ctrl+Z
      // to the field they are in, not to the canvas.
      if (isTyping(event.target)) return
      event.preventDefault()
      if (key === 'y' || event.shiftKey) redo(); else undo()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canDragCards, clearSelection, redo, undo])

  /**
   * Auto Layout is a preview, never a surprise: the tidy tree is shown first and
   * only saved — by clearing every stored coordinate in one request — once it is
   * applied. Cancelling leaves the hand-placed arrangement untouched.
   */
  const startAutoLayoutPreview = () => {
    if (!canManage) return
    setEditError(null)
    setDropNotice(null)
    setAutoLayoutPreview(true)
  }

  /**
   * Where the toolbar's "+" would hang a new box: under the one role that is
   * picked out, and at the top level when nothing is — or when several are, so
   * a group selection never guesses at one of them. The Owner's Box is not a
   * parent, so a new box goes to the top level instead of under it.
   */
  const addRoleParent = useMemo(() => {
    // Several boxes picked out means no single parent, whatever else is open,
    // so the new box goes to the top level rather than under one of them.
    if (selectedSeatIds.length > 1) return null
    const picked = selectedSeatIds.length === 1 ? selectedSeatIds[0] : selectedSeat?.id ?? null
    if (picked === null) return null
    return allSeats.find(seat => seat.id === picked && !seat.is_owners_box) ?? null
  }, [allSeats, selectedSeat, selectedSeatIds])

  const addRoleLabel = addRoleParent
    ? `Add ${indefiniteArticle(LABELS.seat).toLowerCase()} ${LABELS.seat.toLowerCase()} under "${addRoleParent.name}"`
    : `Add ${indefiniteArticle(LABELS.seat).toLowerCase()} ${LABELS.seat.toLowerCase()} at the top level`

  /**
   * The toolbar's "+". It goes through the same create a role's own "Add role
   * below" uses, so the addition is recorded the same way and undo can take it
   * back, and the new box is opened so it can be named straight away.
   */
  const addRoleFromToolbar = () => {
    if (!canManage) return
    if (autoLayoutPreview) { setEditError(AUTO_LAYOUT_BLOCKED); return }
    setEditError(null)
    const parent = addRoleParent
    const record = createRecorder(
      parent ? `add "${NEW_SEAT_NAME}" under "${parent.name}"` : `add "${NEW_SEAT_NAME}"`,
    )
    enqueueChartMutation({
      key: parent ? `seat:${parent.id}:add-child` : 'chart:add-seat',
      url: `${basePath}/seats`,
      method: 'post',
      data: { seat: parent ? { parent_seat_id: parent.id, name: NEW_SEAT_NAME } : { name: NEW_SEAT_NAME } },
      singleFlight: true,
      onSuccess: page => { record(page); openCreatedSeat(page) },
    }, `Unable to add ${indefiniteArticle(LABELS.seat).toLowerCase()} ${LABELS.seat.toLowerCase()}.`)
  }

  const cancelAutoLayoutPreview = () => {
    if (autoLayoutSaving) return
    setAutoLayoutPreview(false)
  }

  const applyAutoLayout = () => {
    if (!canManage || autoLayoutSaving) return
    const placed = allSeats.filter(hasStoredPosition)
    if (placed.length === 0) {
      setAutoLayoutPreview(false)
      setDropNotice('Every box was already in the automatic arrangement, so nothing changed.')
      return
    }
    setEditError(null)
    setAutoLayoutSaving(true)
    // Undo of Auto Layout is every hand placement it threw away, put back.
    const before = placed.map(coordinatesOf)
    const after = placed.map(seat => ({ id: seat.id, x: null, y: null }))
    const accepted = enqueueChartMutation({
      key: 'chart:auto-layout',
      url: `${basePath}/seats/positions`,
      method: 'post',
      data: { positions: placed.map(seat => ({ id: seat.id, x: '', y: '' })) },
      singleFlight: true,
      onSuccess: () => {
        setPendingPositions({})
        setAutoLayoutPreview(false)
        historyRef.current.record({ kind: 'positions', label: 'the automatic layout', before, after })
      },
      onFinish: () => setAutoLayoutSaving(false),
    }, 'Unable to apply the automatic layout.')
    if (!accepted) {
      setAutoLayoutSaving(false)
      setEditError('The automatic layout is still saving. Try again in a moment.')
    }
  }

  const handleRename = () => {
    if (!canManage) { setRenaming(false); return }
    if (!renameValue.trim() || renameValue === chart.name) { setRenaming(false); return }
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:rename',
      url: basePath,
      method: 'patch',
      data: { accountability_chart: { name: renameValue } },
      singleFlight: true,
      onSuccess: () => setRenaming(false),
    }, 'Unable to rename this chart.')
  }

  // Every chart-level mutation is guarded at the helper, not just at the button
  // that normally calls it: a viewer reaching one of these by any other route
  // gets a silent no-op rather than a request the server would reject.
  const archive = () => {
    if (!canManage) return
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:archive',
      url: `${basePath}/archive`,
      method: 'post',
      terminal: true,
      singleFlight: true,
    }, 'Unable to archive this chart.')
  }
  const restore = () => {
    if (!canManage) return
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:restore',
      url: `${basePath}/restore`,
      method: 'post',
      singleFlight: true,
    }, 'Unable to restore this chart.')
  }
  const duplicate = () => {
    if (!canManage) return
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:duplicate',
      url: `${basePath}/duplicate`,
      method: 'post',
      terminal: true,
      singleFlight: true,
    }, 'Unable to duplicate this chart.')
  }
  const deleteChart = () => {
    if (!chart.permissions.can_delete) return
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:delete',
      url: basePath,
      method: 'delete',
      terminal: true,
      singleFlight: true,
    }, 'Unable to delete this chart.')
  }
  const toggleOwnersBox = () => {
    if (!canManage) return
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:owners-box',
      url: `${basePath}/toggle_owners_box`,
      method: 'post',
      singleFlight: true,
    }, "Unable to update the owner's box.")
  }
  const publish = () => {
    if (!chart.permissions.can_publish) return
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:publish',
      url: `${basePath}/publish`,
      method: 'post',
      singleFlight: true,
    }, 'Unable to publish this chart.')
  }
  const unpublish = () => {
    if (!chart.permissions.can_publish) return
    setEditError(null)
    enqueueChartMutation({
      key: 'chart:unpublish',
      url: `${basePath}/unpublish`,
      method: 'post',
      singleFlight: true,
    }, 'Unable to unpublish this chart.')
  }

  const selectedSeatLive = selectedSeat
    ? (chart.seats.find(s => s.id === selectedSeat.id) || (chart.owners_box?.id === selectedSeat.id ? chart.owners_box : null))
    : null

  return (
    <Layout>
      <Head title={chart.name} />

      {/* Chart toolbar */}
      <div className="bg-white border-b border-stone px-6 py-3 flex items-center gap-3 flex-wrap">
        {renaming ? (
          <input
            autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
            onBlur={handleRename} onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') { setRenameValue(chart.name); setRenaming(false) } }}
            className="text-h4 font-semibold text-text-primary border-b border-charcoal outline-none"
          />
        ) : (
          <button onClick={() => canManage && setRenaming(true)} className="text-h4 font-semibold text-text-primary" disabled={!canManage}>
            {chart.name}
          </button>
        )}
        <span className={`px-2 py-0.5 rounded-badge text-xs font-semibold ${chart.chart_state === 'active' ? 'bg-sea/15 text-sea' : 'bg-stone text-text-primary'}`}>
          {chart.chart_state === 'active' ? 'Active' : 'Archived'}
        </span>
        <span className={`px-2 py-0.5 rounded-badge text-xs font-semibold ${chart.is_published ? 'bg-sea/15 text-sea' : 'bg-stone text-text-primary'}`}>
          {chart.is_published ? 'Published' : 'Draft'}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-7 pr-2 py-1.5 border border-stone rounded-button text-sm focus:outline-none focus:border-charcoal"
            />
          </div>
          {canManage && (
            chart.is_published ? (
              <button onClick={unpublish} className="px-3 py-1.5 rounded-button text-sm border border-stone text-text-secondary hover:bg-stone/30 inline-flex items-center gap-1">
                <EyeOff size={14} />Unpublish
              </button>
            ) : (
              <button onClick={publish} className="px-3 py-1.5 rounded-button text-sm border border-sea text-sea bg-sea/10 hover:bg-sea/20 inline-flex items-center gap-1">
                <Globe size={14} />Publish
              </button>
            )
          )}
          <button onClick={toggleOwnersBox} disabled={!canManage}
            className={`px-3 py-1.5 rounded-button text-sm border ${chart.show_owners_box ? 'border-sea text-sea bg-sea/10' : 'border-stone text-text-secondary'} disabled:opacity-50`}>
            Owner's Box
          </button>
          <button
            type="button"
            onClick={() => setShowNavigatorStatus(v => !v)}
            aria-pressed={showNavigatorStatus}
            title={NAVIGATOR_LABELS.toggleHint}
            data-testid="navigator-users-toggle"
            className={`px-3 py-1.5 rounded-button text-sm border inline-flex items-center gap-1.5 ${showNavigatorStatus ? 'border-sea text-sea bg-sea/10' : 'border-stone text-text-secondary'}`}
          >
            <UserCheck size={14} aria-hidden="true" />
            {NAVIGATOR_LABELS.toggle}
          </button>
          <button
            type="button"
            onClick={expandAllCards}
            aria-pressed={density === 'expanded'}
            title={`Show full detail on every ${LABELS.seat.toLowerCase()}`}
            className={`px-2 py-1.5 text-sm border rounded-button hover:bg-stone/30 ${density === 'expanded' ? 'border-sea text-sea bg-sea/10' : 'border-stone text-text-primary'}`}
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAllCards}
            aria-pressed={density === 'collapsed'}
            title={`Show only the ${LABELS.seat.toLowerCase()} name and owners`}
            className={`px-2 py-1.5 text-sm border rounded-button hover:bg-stone/30 ${density === 'collapsed' ? 'border-sea text-sea bg-sea/10' : 'border-stone text-text-primary'}`}
          >
            Collapse all
          </button>
          {canManage && (
            <button
              type="button"
              onClick={startAutoLayoutPreview}
              disabled={autoLayoutPreview}
              title="Preview the automatic tidy arrangement before saving it"
              data-testid="auto-layout-button"
              className="px-2 py-1.5 text-sm border border-stone rounded-button hover:bg-stone/30 inline-flex items-center gap-1 disabled:opacity-50"
            >
              <LayoutGrid size={14} aria-hidden="true" />
              Auto Layout
            </button>
          )}
          <button
            onClick={exportPdf}
            disabled={exporting}
            className="px-2 py-1.5 text-sm border border-stone rounded-button hover:bg-stone/30 inline-flex items-center gap-1 disabled:opacity-50"
            title="Export chart as a one-page landscape PDF"
            data-testid="export-pdf-button"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>

          {canManage && (
            <div className="relative">
              <button ref={chartMenuTriggerRef} onClick={() => setChartMenu(v => !v)} className="p-1.5 border border-stone rounded-button hover:bg-stone/30"><MoreVertical size={16} /></button>
              <FloatingMenu
                open={chartMenu}
                onClose={() => setChartMenu(false)}
                anchorRef={chartMenuTriggerRef}
                width={200}
                className="bg-white shadow-elevated rounded-card border border-stone py-1 min-w-[200px]"
              >
                  <button onClick={() => { setChartMenu(false); setRenaming(true) }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-stone/30 text-text-primary"><Edit2 size={12} className="inline mr-2" />Rename</button>
                  <button onClick={() => { setChartMenu(false); duplicate() }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-stone/30 text-text-primary"><CopyIcon size={12} className="inline mr-2" />Duplicate</button>
                  {chart.chart_state === 'active' && <button onClick={() => { setChartMenu(false); setConfirmModal({ title: 'Archive chart?', message: 'You can restore it later.', variant: 'warning', onConfirm: () => { archive(); setConfirmModal(null) } }) }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-stone/30 text-text-primary"><Archive size={12} className="inline mr-2" />Archive</button>}
                  {chart.chart_state === 'archived' && <button onClick={() => { setChartMenu(false); restore() }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-stone/30 text-text-primary"><RotateCcw size={12} className="inline mr-2" />Restore</button>}
                  {chart.permissions.can_delete && <button onClick={() => { setChartMenu(false); setConfirmModal({ title: 'Delete chart?', message: `This permanently removes the chart and all ${LABELS.seats.toLowerCase()}, ${LABELS.responsibilities.toLowerCase()}, and comments.`, variant: 'danger', onConfirm: () => { deleteChart(); setConfirmModal(null) } }) }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-stone/30 text-pinnacle-red"><Trash2 size={12} className="inline mr-2" />Delete</button>}
              </FloatingMenu>
            </div>
          )}
        </div>
      </div>

      {/* The canvas's own toolbar: a second row of page chrome directly under
          the chart's top bar, not an overlay on the chart. Everything on it
          changes the chart, so it exists only for someone who can — and the
          canvas below is sized for its absence. */}
      {canManage && (
        <div
          role="toolbar"
          aria-label={`${LABELS.seat} canvas actions`}
          data-testid="chart-canvas-toolbar"
          style={{ height: CANVAS_TOOLBAR_HEIGHT }}
          className="bg-white border-b border-stone px-6 py-2 flex items-center gap-2"
        >
          <button
            type="button"
            onClick={undo}
            disabled={!history.canUndo || replaying}
            title={history.undoLabel ? `Undo ${history.undoLabel} (Ctrl+Z)` : 'Nothing to undo'}
            aria-label={history.undoLabel ? `Undo ${history.undoLabel}` : 'Nothing to undo'}
            data-testid="chart-undo-button"
            className="px-2 py-1.5 text-sm border border-stone rounded-button hover:bg-stone/30 inline-flex items-center gap-1 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Undo2 size={14} aria-hidden="true" />
            Undo
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={!history.canRedo || replaying}
            title={history.redoLabel ? `Redo ${history.redoLabel} (Ctrl+Shift+Z)` : 'Nothing to redo'}
            aria-label={history.redoLabel ? `Redo ${history.redoLabel}` : 'Nothing to redo'}
            data-testid="chart-redo-button"
            className="px-2 py-1.5 text-sm border border-stone rounded-button hover:bg-stone/30 inline-flex items-center gap-1 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Redo2 size={14} aria-hidden="true" />
            Redo
          </button>
          <button
            type="button"
            onClick={addRoleFromToolbar}
            title={addRoleLabel}
            aria-label={addRoleLabel}
            data-testid="chart-add-role-button"
            className="px-2 py-1.5 text-sm border border-stone rounded-button hover:bg-stone/30 inline-flex items-center gap-1"
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {showNavigatorStatus && (
        <div
          data-testid="navigator-usage-summary"
          className="bg-white border-b border-stone px-6 py-2 flex items-center gap-4 flex-wrap"
        >
          <span className="text-sm font-semibold text-text-primary">
            {navigatorSummary(chart.navigator_usage.navigator_users, chart.navigator_usage.total_people)}
          </span>
          <NavigatorLegend />
        </div>
      )}

      {autoLayoutPreview && (
        <div
          role="status"
          data-testid="auto-layout-preview-bar"
          className="flex items-center justify-between gap-3 bg-sand border-b border-stone px-6 py-2 text-sm text-text-primary"
        >
          <span>Previewing the automatic arrangement. Nothing is saved until you apply it.</span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={cancelAutoLayoutPreview}
              disabled={autoLayoutSaving}
              data-testid="auto-layout-cancel"
              className="px-3 py-1 rounded-button text-sm border border-stone text-text-primary hover:bg-stone/30 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyAutoLayout}
              disabled={autoLayoutSaving}
              data-testid="auto-layout-apply"
              className="px-3 py-1 rounded-button text-sm border border-sea text-sea bg-sea/10 hover:bg-sea/20 disabled:opacity-50"
            >
              {autoLayoutSaving ? 'Applying…' : 'Apply'}
            </button>
          </span>
        </div>
      )}

      {editError && (
        <div role="alert" className="flex items-center justify-between gap-3 bg-pinnacle-red/10 border-b border-pinnacle-red/30 px-6 py-2 text-sm text-pinnacle-red">
          <span>{editError}</span>
          <button type="button" onClick={() => setEditError(null)} className="text-xs underline">Dismiss</button>
        </div>
      )}

      {dropNotice && (
        <div role="status" className="flex items-center justify-between gap-3 bg-sand border-b border-stone px-6 py-2 text-sm text-text-secondary">
          <span>{dropNotice}</span>
          <button type="button" onClick={() => setDropNotice(null)} className="text-xs underline">Dismiss</button>
        </div>
      )}

      {chart.show_owners_box && chart.owners_box && (
        <div className="bg-sand border-b border-stone px-6 py-3">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-card shadow-card border border-terrain/30 cursor-pointer"
            onClick={() => setSelectedSeat(chart.owners_box!)}
          >
            <span className="text-xs uppercase font-bold text-terrain">Owner's Box</span>
            <span className="text-sm font-semibold text-text-primary">{chart.owners_box.name}</span>
            {chart.owners_box.assignments.length > 0 && (
              showNavigatorStatus ? (
                <span className="text-xs text-text-secondary inline-flex items-center gap-2 flex-wrap">
                  <span aria-hidden="true">·</span>
                  {chart.owners_box.assignments.map(a => (
                    <span key={a.id} className="inline-flex items-center gap-1">
                      {a.display_name}
                      <NavigatorBadge status={a.navigator_status} />
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-xs text-text-secondary">· {chart.owners_box.assignments.map(a => a.display_name).join(', ')}</span>
              )
            )}
          </div>
        </div>
      )}

      <div
        ref={paneRef}
        onPointerDownCapture={notePointerDown}
        style={{
          // The canvas toolbar is a row above the pane, not an overlay on it,
          // so its height comes out of the canvas — and only for the people
          // who get the row, or a viewer is left with a blank strip.
          height: `calc(100vh - ${CHART_CHROME_HEIGHT + (canManage ? CANVAS_TOOLBAR_HEIGHT : 0)}px)`,
          minHeight: 500,
        }}
        className="relative bg-bg-secondary"
      >
        <ReactFlow
          nodes={presentationNodes}
          edges={presentationEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onPaneClick={deselectConnector}
          onNodeClick={deselectConnector}
          onNodeDragStart={canDragCards ? onNodeDragStart : undefined}
          onNodeDrag={canDragCards ? onNodeDrag : undefined}
          onNodeDragStop={canDragCards ? onNodeDragStop : undefined}
          onSelectionDragStart={canDragCards ? onSelectionDragStart : undefined}
          onSelectionDrag={canDragCards ? onSelectionDrag : undefined}
          onSelectionDragStop={canDragCards ? onSelectionDragStop : undefined}
          onSelectionChange={handleSelectionChange}
          onSelectionStart={noteRubberBandStart}
          className="chart-canvas"
          // Left-drag on empty canvas draws the selection rectangle for an
          // editor, so panning moves to the middle and right buttons (and the
          // minimap and zoom controls, which nothing here changes). A viewer
          // has no rectangle to draw, so their left-drag still pans.
          panOnDrag={canDragCards ? [1, 2] : true}
          selectionOnDrag={canDragCards}
          // Shift is the key that adds a box to the selection. React Flow
          // otherwise uses it for the rubber band too, and a Shift+click on a
          // card would then start a rectangle instead of selecting the card.
          selectionKeyCode={null}
          multiSelectionKeyCode={canDragCards ? 'Shift' : null}
          // "Every box it touches", not "every box it swallows".
          selectionMode={SelectionMode.Partial}
          elementsSelectable={canDragCards}
          // Removing a box is a confirmed action from its own menu, never a
          // keystroke: the canvas's own delete would drop it from the picture
          // without ever telling the server.
          deleteKeyCode={null}
          // Wheel zooming is `useSmoothWheelZoom`'s: React Flow's own applies a
          // notch as a jump, and two handlers on one wheel would double it.
          zoomOnScroll={false}
          // No `fitView`: it frames the cards' bounding box, which would undo
          // the centring of the top row and can zoom past the point where the
          // obsession names are readable. `fitChartCanvas` owns the camera.
          onInit={handleFlowInit}
          minZoom={CHART_MIN_ZOOM}
          maxZoom={CHART_MAX_ZOOM}
          // Pan, zoom and selection stay on for everyone; only moving a card is
          // an edit, so a viewer's canvas simply has nothing to pick up.
          nodesDraggable={canDragCards}
          nodesConnectable={false}
          nodeDragThreshold={4}
        >
          {/* The resting grid IS the snap lattice — same spacing AND same
              phase, so every dot someone aims at is a dot a box will settle on.
              The offset is what puts the dots on the intersections instead of
              in the middle of the cells; without it the grid is drawn a half
              cell out and nothing on it is reachable. It stays as faint as it
              has always been: the gesture is what makes the grid visible, not
              the chart at rest. */}
          <Background
            gap={GUIDELINE_SPACING}
            size={GUIDELINE_DOT_SIZE}
            offset={guidelineDotOffset(GUIDELINE_DOT_SIZE)}
            color="#E6E6E6"
          />
          {/* Guidelines exist only for the length of a gesture, and only for
              someone who can actually move a box. Same lattice, same phase,
              drawn a little more present. */}
          {canDragCards && draggingSeatId !== null && (
            <Background
              id="chart-guidelines"
              className="chart-guidelines"
              variant={BackgroundVariant.Dots}
              gap={GUIDELINE_SPACING}
              size={GUIDELINE_DRAG_DOT_SIZE}
              offset={guidelineDotOffset(GUIDELINE_DRAG_DOT_SIZE)}
              color="#9A9A9A"
            />
          )}
          <Controls showInteractive={false} />
          {/* The bottom-left cluster: the zoom buttons keep their corner, the
              minimap sits beside them and the readout above both. Reading and
              navigating are not editing, so a viewer gets all three. */}
          <MiniMap
            data-testid="chart-minimap"
            ariaLabel="Chart minimap"
            pannable
            onClick={(_event, point) => centerOnFlowPoint(point)}
            bgColor="#FFFFFF"
            nodeColor="#351E1D"
            nodeStrokeColor="#351E1D"
            nodeStrokeWidth={2}
            maskColor="rgba(53, 30, 29, 0.12)"
            maskStrokeColor="#351E1D"
            maskStrokeWidth={1}
            className="!m-0 rounded-card border border-stone shadow-card"
            style={{ left: 56, bottom: 15, margin: 0, width: 180, height: 120 }}
          />
          <ZoomReadout />
        </ReactFlow>
        {canEditConnectors && newLineDrag && drawnEndpoint && (() => {
          const origin = layoutRef.current.find(node => node.seat.id === newLineDrag.originSeatId)
          if (!origin) return null
          const sourceX = origin.x + origin.width / 2
          const sourceY = newLineDrag.direction === 'top' ? origin.y : origin.y + origin.height
          const { x, y, zoom } = getViewport()
          // Only the illustration is eased. Release always resolves against
          // the unmodified pointer held by the window listener above.
          const path = newLineDrag.direction === 'bottom'
            ? dragCurvePath(sourceX, sourceY, drawnEndpoint.x, drawnEndpoint.y)
            : `M ${sourceX},${sourceY} C ${sourceX},${sourceY - 55} ${drawnEndpoint.x},${drawnEndpoint.y + 55} ${drawnEndpoint.x},${drawnEndpoint.y}`
          return (
            <svg
              data-testid="new-connector-drag"
              className="absolute inset-0 h-full w-full pointer-events-none"
              style={{ zIndex: 1002 }}
            >
              <g transform={`translate(${x} ${y}) scale(${zoom})`}>
                <path d={path} fill="none" stroke={CONNECTOR_SELECTED_COLOR} strokeWidth={2.5} strokeDasharray="6 4" />
                <circle cx={drawnEndpoint.x} cy={drawnEndpoint.y} r={5} fill="#FFFFFF" stroke={CONNECTOR_SELECTED_COLOR} strokeWidth={2} />
              </g>
            </svg>
          )
        })()}
      </div>

      {/* The line the box being carried is about to land on, drawn over the
          canvas for as long as the gesture is within reach of it. */}
      {canDragCards && <GuidelineSnapLayer lines={snapLines} paneRef={paneRef} />}

      {canDragCards && (
        <SelectionToolbarLayer
          boxes={selectionBoxes}
          paneRef={paneRef}
          hidden={draggingSeatId !== null}
          busy={aligning || replaying}
          onApply={applySelectionAlignment}
        />
      )}

      {selectedSeatLive && (
        <SeatDetailPanel
          chartId={chart.id}
          seat={selectedSeatLive}
          canEdit={selectedSeatLive.can_edit}
          pickerTeam={user_picker_team}
          pickerOrg={user_picker_org}
          orgTags={org_tags}
          onClose={() => setSelectedSeat(null)}
          onDuplicate={(s) => handleSeatAction('duplicate', s)}
          onDelete={(s) => handleSeatAction('delete', s)}
          showFillToast={showFillToast}
          showNavigatorStatus={showNavigatorStatus}
           onEditError={(errors, fallback) => reportEditError(errors, fallback || `Unable to update this ${LABELS.seat.toLowerCase()}.`)}
           onFieldEdit={recordFieldEdit}
           mutationQueue={mutationQueue}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          isOpen
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant || 'danger'}
          onConfirm={confirmModal.onConfirm}
          onClose={() => setConfirmModal(null)}
        />
      )}

      {movePicker && (
        <MoveSeatPicker
          seat={movePicker.seat}
          allSeats={allSeats}
          onClose={() => setMovePicker(null)}
          onMove={(parentId) => {
            setEditError(null)
            // Redirecting an arrow is the same change as re-parenting from the
            // picker, but it must keep the box where it is and confirm before
            // leaving it at the top level.
            if (movePicker.viaConnector) {
              const seat = movePicker.seat
              setMovePicker(null)
              setSelectedEdgeId(null)
              if (parentId === null) confirmDetach(seat.id)
              else submitConnectorMove(seat.id, parentId, siblingsOf(allSeats, parentId).filter(s => s.id !== seat.id).length)
              return
            }
            // The picker's copy of the box can be a few props behind, so the
            // history entry is built from the one on screen right now.
            const moved = allSeats.find(s => s.id === movePicker.seat.id) ?? movePicker.seat
            const destination = parentId === null ? null : allSeats.find(s => s.id === parentId)
            const recordMove = menuMoveRecorder(
              moved,
              destination
                ? `move "${moved.name}" to "${destination.name}"`
                : `move "${moved.name}" to the top level`,
            )
            enqueueChartMutation({
              key: `seat:${moved.id}:move`,
              url: `${basePath}/seats/${moved.id}/move`,
              method: 'post',
              // Picking a new parent is a change to the reporting line alone, so
              // a hand-placed box sends the spot it already holds; an unplaced
              // one sends none and the tidy layout keeps deciding.
              data: { parent_seat_id: parentId, position: 0, ...heldPlacementParams(moved) },
              singleFlight: true,
              onSuccess: () => { setMovePicker(null); recordMove() },
            }, `Unable to move this ${LABELS.seat.toLowerCase()}.`)
          }}
        />
      )}

      {/* Right-clicking an arrow. The menu is anchored to the point that was
          clicked, which is what a context menu is anchored to. */}
      {edgeMenu && canEditConnectors && (
        <>
          <div
            ref={edgeMenuAnchorRef}
            style={{ position: 'fixed', top: edgeMenu.y, left: edgeMenu.x, width: 1, height: 1 }}
            aria-hidden="true"
          />
          <FloatingMenu
            open
            onClose={() => setEdgeMenu(null)}
            anchorRef={edgeMenuAnchorRef}
            width={240}
            align="start"
            className="bg-white border border-stone rounded-card shadow-elevated py-1"
            style={{ width: 240 }}
          >
            <div className="px-3 py-1.5 text-xs text-text-secondary border-b border-stone">
              {allSeats.find(s => s.id === edgeMenu.childSeatId)?.name} → {allSeats.find(s => s.id === edgeMenu.parentSeatId)?.name}
            </div>
            <button
              type="button"
              data-testid="connector-menu-redirect"
              className="block w-full text-left px-3 py-1.5 text-sm text-text-primary hover:bg-stone/30"
              onClick={() => {
                const child = allSeats.find(s => s.id === edgeMenu.childSeatId)
                setEdgeMenu(null)
                if (child) setMovePicker({ seat: child, viaConnector: true })
              }}
            >
              Redirect to another {LABELS.seat.toLowerCase()}…
            </button>
            <button
              type="button"
              data-testid="connector-menu-delete"
              className="block w-full text-left px-3 py-1.5 text-sm text-pinnacle-red hover:bg-stone/30"
              onClick={() => {
                const childSeatId = edgeMenu.childSeatId
                setEdgeMenu(null)
                confirmDetach(childSeatId)
              }}
            >
              <Trash2 size={12} className="inline mr-2" />Delete reporting line
            </button>
          </FloatingMenu>
        </>
      )}

      {fillToast && canOfferFill && (
        <div className="fixed bottom-6 right-6 z-50 bg-white border border-sea shadow-elevated rounded-card p-4 max-w-sm">
          <p className="text-sm text-text-primary">Mark "<strong>{fillCandidate?.name ?? fillToast.name}</strong>" as Filled?</p>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setFillToast(null)} className="text-xs text-text-secondary px-2 py-1">Not now</button>
            <button onClick={confirmFill} className="text-xs bg-sea text-white px-3 py-1 rounded">Mark Filled</button>
          </div>
        </div>
      )}
    </Layout>
  )
}

function MoveSeatPicker({ seat, allSeats, onClose, onMove }: { seat: SeatDetail; allSeats: SeatDetail[]; onClose: () => void; onMove: (parentId: number | null) => void }) {
  // Exclude this seat and its subtree
  const excluded = new Set<number>([seat.id])
  let changed = true
  while (changed) {
    changed = false
    allSeats.forEach(s => {
      if (s.parent_seat_id && excluded.has(s.parent_seat_id) && !excluded.has(s.id)) {
        excluded.add(s.id); changed = true
      }
    })
  }
  const candidates = allSeats.filter(s => !excluded.has(s.id) && !s.is_owners_box)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-charcoal/50" onClick={onClose} />
      <div className="relative bg-white rounded-card shadow-elevated w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-stone flex items-center justify-between">
          <h3 className="text-h4 font-semibold text-text-primary">Move {seat.name} to…</h3>
          <button onClick={onClose} className="p-1 hover:bg-stone/40 rounded">×</button>
        </div>
        <div className="overflow-y-auto p-2">
          <button onClick={() => onMove(null)} className="w-full text-left px-3 py-2 hover:bg-stone/30 rounded text-sm">
            <span className="text-text-secondary italic">(Top level — no parent)</span>
          </button>
          {candidates.map(s => (
            <button key={s.id} onClick={() => onMove(s.id)} className="w-full text-left px-3 py-2 hover:bg-stone/30 rounded text-sm text-text-primary">
              {s.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
